import React, { useEffect, useMemo, useState, useRef } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Users, Car as CarIcon, CalendarDays, Search, AlertTriangle, CheckCircle2, Layers, Plus, Send, Trash2 } from "lucide-react";
import html2canvas from "html2canvas";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { fmtRoDate } from "../../lib/dateFmt";
import {
  DAY_NAMES, MONTH_NAMES,
  COLORS, INVENTORY_SET_COLORS, PROJECT_INACTIVE_CATEGORIES, DISMANTLE_WINDOW_DAYS,
  toISO, mondayOf, addDays, fmtRoDay, dowFromIso, isSaturdayIso, fmtRangeLabel,
} from "./utils";
import { regieDefaultMinForIso } from "./utils";
import QuickEditPopover from "./QuickEditPopover";
import DismantleDialog from "./DismantleDialog";
import RegieFullDialog from "./RegieFullDialog";
import EditMinPeopleDialog from "./EditMinPeopleDialog";
import PersonAvailabilityMenu from "./PersonAvailabilityMenu";
import { RegieDetailDialog, ProjectDetailDialog } from "../projects/ProjectSubcomponents";

/* eslint-disable no-unused-vars */

export default function CreateDayDialog({ day, projects, regii = [], persons, cars, entries = [], regiiWeeks = [], regiiDayOverrides = [], projectsDayOverrides = [], onClose, onSaved, onRefresh }) {
  const dayIso = toISO(day);
  const [historyFor, setHistoryFor] = useState(null); // { type: "regie"|"project", ref_id, label }
  // Filtrează proiectele active în ziua respectivă
  // Reguli:
  //  - cat. 6.1 (Finalizat – De demontat): vizibil 14 zile după finalizare (pentru demontaj)
  //  - cat. 7,8,9,10: ascuns
  //  - cat 6.1 (de demontat): ANY zi >= finalizare, fără limită upper (utilizatorul cere să rămână disponibile pentru alocare la demontaj)
  //  - cat 6 (în lucru): start_date <= day, fără limită upper (proiectul rămâne disponibil pentru alocări de demontaj chiar și după end_date)
  // Cutoff demontaj: șantierele finalizate înainte de această dată nu mai apar ca opțiuni de demontaj.
  // Cerut explicit de utilizator pentru a curăța lista de proiecte vechi care altfel ar rămâne în „cat 6.1" la nesfârșit.
  const DISMANTLE_CUTOFF = "2026-05-01";

  // Helper: data efectivă a finalizării unui proiect (folosită pentru a calcula „dismantle day = finalizare + 1").
  const finalizationDateOf = (p) => {
    const raw = p.finalization?.finalized_at || p.status_changed_at || p.end_date || null;
    return raw ? String(raw).slice(0, 10) : null;
  };
  // Helper: data în care proiectul „intră" la demontaj — ziua imediat după finalizare/end_date.
  const dismantleDayOf = (p) => {
    const fin = finalizationDateOf(p);
    if (!fin) return null;
    try {
      const d = new Date(fin + "T00:00:00");
      if (isNaN(d.getTime())) return null;
      d.setDate(d.getDate() + 1);
      const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), dd = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    } catch { return null; }
  };

  // Sâmbătă: granița săptămânii (lu→du) ca să filtrăm doar proiecte/regii active în această săptămână.
  // DECLARAT ÎNAINTE de eligibleProjects / eligibleRegii pentru a evita TDZ (ReferenceError la render).
  const weekBounds = useMemo(() => {
    try {
      const d = new Date(day + "T00:00:00");
      // dayOfWeek: 0=Du, 1=Lu, ..., 6=Sâ → shift ca să primim lunea săptămânii
      const dow = d.getDay();
      const daysFromMonday = dow === 0 ? 6 : dow - 1;
      const monday = new Date(d);
      monday.setDate(d.getDate() - daysFromMonday);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
      return { monday: fmt(monday), sunday: fmt(sunday), isSat: dow === 6 };
    } catch {
      return { monday: day, sunday: day, isSat: false };
    }
  }, [day]);

  const eligibleProjects = useMemo(() => {
    const isSat = (() => {
      try { return new Date(day + "T00:00:00").getDay() === 6; } catch { return false; }
    })();
    // Săptămâna curentă lu→du (sâmbătă: filtru relaxat la nivel de săptămână)
    let weekMon = day, weekSun = day;
    if (isSat) {
      try {
        const d = new Date(day + "T00:00:00");
        const mon = new Date(d); mon.setDate(d.getDate() - 5);
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
        weekMon = fmt(mon); weekSun = fmt(sun);
      } catch { /* fallthrough */ }
    }
    // Sâmbătă: pentru a NU mai inunda dialog-ul cu zeci de proiecte deschise,
    // restrângem la proiectele care chiar au alocări planificate în săptămâna
    // curentă (Lu→Du, oricare zi a săptămânii — luate din `entries`).
    const weekProjectIds = isSat ? new Set(
      (entries || [])
        .filter((e) => e.project_id && (e.start_date || "") <= weekSun && (e.end_date || e.start_date || "") >= weekMon)
        .map((e) => e.project_id)
    ) : null;
    return (projects || []).filter((p) => {
      // MAGAZIA — mereu disponibilă pentru alocare rapidă (persoane libere → Magazie)
      if (p.is_magazia) return true;
      const cat = String(p.category || "");
      // Cat 7 (demontat): afișează DOAR în ziua exactă a demontării
      if (cat === "7") {
        return p.dismantle_date && p.dismantle_date === day;
      }
      if (PROJECT_INACTIVE_CATEGORIES.includes(cat)) return false;
      if (cat === "6.1") {
        // Pe sâmbătă: NU afișăm proiecte „de demontat" — doar șantiere ACTIVE în săptămâna curentă.
        if (isSat) return false;
        const dismantleDay = dismantleDayOf(p);
        if (!dismantleDay) return false;
        if (dismantleDay < DISMANTLE_CUTOFF) return false;
        return dismantleDay === day;
      }
      // cat 6 (în lucru): pe sâmbătă afișăm DOAR șantierele care au fost
      // efectiv alocate într-o zi a săptămânii curente (Lu→Du).
      if (!p.start_date) return false;
      if (isSat) {
        return weekProjectIds && weekProjectIds.has(p.id);
      }
      // Lu-Vi: comportament neschimbat — ziua trebuie să fie între start și end_date
      if (p.end_date && p.end_date < day) {
        const dismantleDay = dismantleDayOf(p);
        if (!dismantleDay) return false;
        if (dismantleDay < DISMANTLE_CUTOFF) return false;
        return dismantleDay === day;
      }
      return p.start_date <= day && (!p.end_date || day <= p.end_date);
    });
  }, [projects, day, entries]);

  const eligibleRegii = useMemo(() => {
    return (regii || []).filter((r) => {
      if (r.active === false) return false;
      // Pauză: dacă există intervale, verifică doar dacă ZIUA curentă cade într-un interval activ.
      // Dacă end_date al pauzei a trecut, regia redevine eligibilă automat (fără click pe Reluare).
      const pauses = Array.isArray(r.pauses) ? r.pauses : [];
      if (pauses.length > 0) {
        const inPause = pauses.some((p) => {
          const sd = p.start_date || "";
          const ed = p.end_date || "9999-12-31";
          return sd && sd <= day && day <= ed;
        });
        if (inPause) return false;
      } else if (r.paused === true) {
        // Legacy: paused boolean fără interval — blochează complet
        return false;
      }
      // Regie fără date = activă oricând
      if (!r.start_date && !r.end_date) return true;
      const sd = r.start_date || day;
      const ed = r.indeterminate_end ? "9999-12-31" : (r.end_date || day);
      // SÂMBĂTĂ: filtru relaxat — afișează regiile active oricând în săptămâna curentă (lu→du),
      // nu doar cele care „aterizează" exact pe sâmbătă. Cerut explicit de utilizator: pe sâmbătă
      // se planifică doar regiile săptămânii curente, nu tot universul.
      if (weekBounds.isSat) {
        return sd <= weekBounds.sunday && ed >= weekBounds.monday;
      }
      return sd <= day && day <= ed;
    });
  }, [regii, day, weekBounds]);

  // Regii aflate ÎN PAUZĂ pe această zi — afișate separat ca să poată fi scoase din pauză.
  const pausedRegii = useMemo(() => {
    return (regii || []).filter((r) => {
      if (r.active === false) return false;
      const pauses = Array.isArray(r.pauses) ? r.pauses : [];
      const inPause = pauses.length > 0
        ? pauses.some((p) => (p.start_date || "") <= day && day <= (p.end_date || "9999-12-31"))
        : r.paused === true;
      if (!inPause) return false;
      if (!r.start_date && !r.end_date) return true;
      const sd = r.start_date || day;
      const ed = r.indeterminate_end ? "9999-12-31" : (r.end_date || day);
      return sd <= day && day <= ed;
    });
  }, [regii, day]);

  const dayEntries = useMemo(() => {
    const isSat = (() => {
      try { return new Date(day + "T00:00:00").getDay() === 6; } catch { return false; }
    })();
    if (isSat) {
      return (entries || []).filter((e) => e.start_date === day && e.end_date === day);
    }
    return (entries || []).filter((e) => (e.start_date || "") <= day && day <= (e.end_date || ""));
  }, [entries, day]);

  // Resolve effective min/max for a regie on this `day` with priority:
  //   1) per-day override (regii_day_overrides) — most specific
  //   2) per-week override (regii_weeks) where week_start = monday(day)
  //   3) per-day-of-week default (regieDefaultMinForIso — built-in)
  //   4) regie's default_min_people / default_max_people
  const _weekMondayIso = useMemo(() => {
    try { return toISO(mondayOf(new Date(day + "T00:00:00"))); } catch { return null; }
  }, [day]);

  const resolveRegieMin = (r) => {
    const dov = (regiiDayOverrides || []).find((o) => o.regie_id === r.id && o.date === day);
    if (dov && dov.min_people != null) return Number(dov.min_people);
    const wkOv = (regiiWeeks || []).find((w) => w.regie_id === r.id && w.week_start === _weekMondayIso);
    if (wkOv && wkOv.min_people != null) return Number(wkOv.min_people);
    return Number(regieDefaultMinForIso(r, day) || r.default_min_people || 0);
  };

  const resolveRegieMax = (r) => {
    const dov = (regiiDayOverrides || []).find((o) => o.regie_id === r.id && o.date === day);
    if (dov && dov.max_people != null) return Number(dov.max_people);
    const wkOv = (regiiWeeks || []).find((w) => w.regie_id === r.id && w.week_start === _weekMondayIso);
    if (wkOv && wkOv.max_people != null) return Number(wkOv.max_people);
    return Number(r.default_max_people || 0);
  };

  // Cards: cheie unică per (type, ref_id) sau per entry orfan
  const initialCards = useMemo(() => {
    const out = [];
    // Regii
    for (const r of eligibleRegii) {
      const existing = dayEntries.find((e) => e.regie_id === r.id || (e.is_regie && e.label === (r.name || r.label)));
      out.push({
        key: `regie-${r.id}`,
        type: "regie",
        ref_id: r.id,
        label: r.name || r.label || "Regie",
        color: r.color || "#F59E0B",
        is_regie: true,
        firma: r.firma,
        adresa: r.adresa,
        vorarbeiter: r.vorarbeiter,
        note: existing?.note || "",
        min_people: resolveRegieMin(r),
        max_people: resolveRegieMax(r),
        min_cars: r.default_min_cars || 0,
        start_date: r.start_date || null,
        end_date: r.end_date || null,
        indeterminate_end: !!r.indeterminate_end,
        pauses: r.pauses || [],
        entryId: existing?.id || null,
        person_ids: existing?.person_ids || [],
        car_ids: existing?.car_ids || [],
        car_drivers: existing?.car_drivers || {},
        allocation_color: existing?.allocation_color || null,
        person_splits: existing?.person_splits || {},
        share_pids: [],
        dirty: false,
      });
    }
    // Proiecte
    for (const p of eligibleProjects) {
      const existing = dayEntries.find((e) => e.project_id === p.id);
      // is_dismantle: cat 6.1 SAU end_date trecut (proiectul a fost finalizat → mai pot face doar demontaj)
      const endPast = p.end_date && p.end_date < day;
      const isDismantle = String(p.category || "") === "6.1" || endPast;
      const isDismantled = String(p.category || "") === "7";  // demontat efectiv
      // Nu mai e nevoie de personal după end_date, demontare, sau pentru cat 6.1 (în stare de demontaj)
      // Demontajul se face după muncă / cu oameni liberi — nu trebuie să fie cerut ca minim.
      const pdov = (projectsDayOverrides || []).find((o) => o.project_id === p.id && o.date === day);
      const projMin = (endPast || isDismantled || isDismantle)
        ? 0
        : (pdov && pdov.min_people != null ? Number(pdov.min_people) : Number(p.min_people || 0));
      const projMax = (endPast || isDismantled || isDismantle)
        ? 0
        : (pdov && pdov.max_people != null ? Number(pdov.max_people) : Number(p.max_people || 0));
      out.push({
        key: `proj-${p.id}`,
        type: "project",
        ref_id: p.id,
        label: isDismantled ? `✅ DEMONTAT: ${p.name}` : isDismantle ? `🔧 DEMONTAJ: ${p.name}` : p.name,
        color: isDismantled ? "#16A34A" : isDismantle ? "#EA580C" : (p.color || "#3B82F6"),
        is_regie: false,
        is_dismantle: isDismantle,
        is_dismantled: isDismantled,
        is_end_past: endPast,
        adresa: p.adresa,
        vorarbeiter: p.vorarbeiter,
        description: p.description,
        work_zones: p.work_zones,
        special: p.special,
        // Detalii suplimentare pentru afișare la „alocă persoane" (PAS 2 dialog)
        zones: p.zones || [],
        scope_items: p.scope_items || [],
        is_renovation: p.is_renovation,
        bags_festgebunden: p.bags_festgebunden || 0,
        bags_schwachgebunden: p.bags_schwachgebunden || 0,
        requires_post_measurement: p.requires_post_measurement,
        is_ruckbau: p.is_ruckbau || false,
        bauherr: p.bauherr || "",
        category: p.category,
        start_date: p.start_date || null,
        end_date: p.end_date || null,
        // Data finalizării (folosită ca bază pentru sugestia datei de demontaj = finalizare + 1 zi)
        finalization_date: p.finalization?.finalized_at ? String(p.finalization.finalized_at).slice(0, 10)
          : p.status_changed_at ? String(p.status_changed_at).slice(0, 10)
          : (p.end_date || null),
        // Pentru demontate: afișăm info despre cine și când
        dismantle_date: p.dismantle_date,
        dismantled_at: p.dismantled_at,
        dismantled_by_user: p.dismantled_by_user,
        dismantled_by_person_ids: p.dismantled_by_person_ids,
        dismantled_note: p.dismantled_note,
        min_people: projMin,
        max_people: projMax,
        min_cars: (endPast || isDismantled || isDismantle) ? 0 : (p.min_cars || 0),
        entryId: existing?.id || null,
        person_ids: existing?.person_ids || [],
        car_ids: existing?.car_ids || [],
        car_drivers: existing?.car_drivers || {},  // { car_id: person_id }
        allocation_color: existing?.allocation_color || null,
        person_splits: existing?.person_splits || {},
        share_pids: [],
        note: existing?.note || "",
        dirty: false,
      });
    }
    return out;
  }, [eligibleRegii, eligibleProjects, dayEntries, regiiWeeks, regiiDayOverrides, projectsDayOverrides, _weekMondayIso]); // eslint-disable-line react-hooks/exhaustive-deps

  const [cards, setCards] = useState(initialCards);
  useEffect(() => { setCards(initialCards); /* eslint-disable-next-line */ }, [day]);
  // Sincronizează min/max (necesar persoane/mașini) când datele se reîmprospătează
  // (ex. după salvarea din EditMinPeopleDialog) — FĂRĂ a pierde selecțiile locale (dirty).
  useEffect(() => {
    setCards((prev) => prev.map((c) => {
      const fresh = initialCards.find((f) => f.key === c.key);
      if (!fresh) return c;
      if (fresh.min_people !== c.min_people || fresh.max_people !== c.max_people || fresh.min_cars !== c.min_cars) {
        return { ...c, min_people: fresh.min_people, max_people: fresh.max_people, min_cars: fresh.min_cars };
      }
      return c;
    }));
    /* eslint-disable-next-line */
  }, [initialCards]);

  const [selectedKey, setSelectedKey] = useState(null);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  useEffect(() => { setShowNoteEditor(false); }, [selectedKey]);
  const [tab, setTab] = useState("persons"); // "persons" | "cars"
  const [qp, setQp] = useState("");
  const [qc, setQc] = useState("");
  const [savingKeys, setSavingKeys] = useState(new Set()); // chei carduri în curs de salvare
  const [savedTickKeys, setSavedTickKeys] = useState(new Set()); // chei carduri salvate recent (pentru tick verde scurt)
  const saveTimersRef = React.useRef({}); // key -> timeout id (debounce auto-save)
  const cardsRef = React.useRef(cards);
  useEffect(() => { cardsRef.current = cards; }, [cards]);

  // Numărul de carduri dirty (cu modificări nesalvate) — pentru indicator UI
  const dirtyCount = cards.filter((c) => c.dirty).length;
  const savingCount = savingKeys.size;

  // Flush sincron al tuturor cardurilor dirty (la închidere/page hide/beforeunload)
  // Folosește sendBeacon pentru a transmite chiar dacă pagina se închide.
  const flushAllPendingSync = React.useCallback(() => {
    const timers = saveTimersRef.current;
    const dirtyCards = cardsRef.current.filter((c) => c.dirty);
    if (dirtyCards.length === 0) return;
    // Clear pending debounce-uri și salvează imediat
    Object.entries(timers).forEach(([k, id]) => {
      clearTimeout(id);
      delete timers[k];
    });
    // sendBeacon pentru fiabilitate maximă pe mobile (chiar dacă tab-ul se închide)
    const apiUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const token = sessionStorage.getItem("convic_token") || localStorage.getItem("convic_token");
    for (const c of dirtyCards) {
      const payload = {
        project_id: c.type === "project" ? c.ref_id : null,
        regie_id: c.type === "regie" ? c.ref_id : null,
        label: c.label,
        is_regie: c.is_regie,
        color: c.color,
        day,
        person_ids: c.person_ids,
        car_ids: c.car_ids,
        car_drivers: c.car_drivers || {},
        allocation_color: c.allocation_color || null,
        note: c.note || null,
        share_person_ids: c.share_pids || [],
      };
      // fetch keepalive — supraviețuiește închiderii tab-ului (mobile-safe).
      // (sendBeacon nu permite Authorization headers — așa că folosim fetch direct.)
      try {
        fetch(`${apiUrl}/api/schedule/upsert-from-day`, {
          method: "POST",
          body: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          keepalive: true,
        }).catch(() => {});
      } catch { /* ignore */ }
    }
  }, [day]);

  // Listener-i pentru evenimente critice mobile: vizibilitate ascunsă, before-unload
  useEffect(() => {
    const onHide = () => flushAllPendingSync();
    const onBeforeUnload = (e) => {
      if (cardsRef.current.some((c) => c.dirty) || saveTimersRef.current && Object.keys(saveTimersRef.current).length > 0) {
        flushAllPendingSync();
        e.preventDefault();
        e.returnValue = "Salvare în curs — așteaptă câteva secunde...";
        return e.returnValue;
      }
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [flushAllPendingSync]);

  // Dialog de selecție șofer când sunt mai mulți persoane cu permis în echipă
  // și mașina nu are deja un șofer desemnat. {cardKey, carId, carLabel, candidates: [{id,name}]}
  const [driverPickPending, setDriverPickPending] = useState(null);
  const driverPickResolverRef = React.useRef(null);

  const regiiCards = cards.filter((c) => c.type === "regie");
  const projectCards = useMemo(() => {
    const arr = cards.filter((c) => c.type === "project");
    // Sortare cerută explicit: proiecte ACTIVE sus, proiecte de DEMONTAJ jos.
    // Demontaj = card-uri cu is_dismantle === true sau is_dismantled === true.
    const dismantleRank = (c) => {
      if (c.is_dismantled) return 2; // marcat deja Demontat (cat 7)
      if (c.is_dismantle) return 1;  // de demontat (cat 6.1 sau end_past)
      return 0;                       // activ — sus
    };
    return [...arr].sort((a, b) => {
      const ra = dismantleRank(a);
      const rb = dismantleRank(b);
      if (ra !== rb) return ra - rb;
      // În interiorul grupului — sortare alfabetică pe label (stabilă, predictibilă)
      return (a.label || "").localeCompare(b.label || "", "ro");
    });
  }, [cards]);

  // Prioritate firme (la fel ca în Planificator) + grupare regii pe firmă
  const FIRMA_PRIORITY = ["implenia", "imas", "speztec", "klarotec", "maba"];
  const firmaPriorityRank = (f) => {
    const k = (f || "").trim().toLowerCase();
    const idx = FIRMA_PRIORITY.indexOf(k);
    return idx === -1 ? 999 : idx;
  };
  const regiiByFirma = useMemo(() => {
    const groups = new Map();
    for (const c of regiiCards) {
      const key = (c.firma || "").trim() || "Fără firmă";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    }
    // Sortare grupuri: priority first, apoi alfabetic
    return Array.from(groups.entries()).sort(([a], [b]) => {
      const ra = firmaPriorityRank(a);
      const rb = firmaPriorityRank(b);
      if (ra !== rb) return ra - rb;
      return a.localeCompare(b, "ro");
    });
  }, [regiiCards]);
  const selectedCard = cards.find((c) => c.key === selectedKey) || null;

  // 🔁 Persoane care au lucrat deja pe regia/proiectul selectat → pulsare amber
  const [pastWorkers, setPastWorkers] = useState({}); // { person_id: hours }
  useEffect(() => {
    if (!selectedCard?.ref_id || (selectedCard.type !== "regie" && selectedCard.type !== "project")) {
      setPastWorkers({});
      return;
    }
    const param = selectedCard.type === "regie" ? `regie_id=${selectedCard.ref_id}` : `project_id=${selectedCard.ref_id}`;
    api.get(`/planificator/past-workers?${param}`)
      .then((r) => {
        const m = {};
        for (const w of (r.data?.workers || [])) m[w.person_id] = w.hours;
        setPastWorkers(m);
      })
      .catch(() => setPastWorkers({}));
  }, [selectedCard?.ref_id, selectedCard?.type]);

  // 🚗 Afinitate șofer–mașină (din istoricul car_drivers): sugestii pulsante verzi.
  const [affinity, setAffinity] = useState({ car_top_drivers: {}, driver_top_cars: {} });
  useEffect(() => {
    api.get("/planificator/driver-affinity").then((r) => setAffinity(r.data || { car_top_drivers: {}, driver_top_cars: {} })).catch(() => {});
  }, []);
  // Mașinile sugerate = mașina cea mai condusă recent de fiecare șofer alocat pe card
  const suggestedCarIds = useMemo(() => {
    const out = new Set();
    if (!selectedCard) return out;
    for (const pid of selectedCard.person_ids || []) {
      const top = (affinity.driver_top_cars?.[pid] || [])[0];
      if (top && !(selectedCard.car_ids || []).includes(top)) out.add(top);
    }
    return out;
  }, [selectedCard, affinity]);
  // Șoferii sugerați = șoferul obișnuit al fiecărei mașini alocate pe card
  const suggestedDriverIds = useMemo(() => {
    const out = new Set();
    if (!selectedCard) return out;
    for (const cid of selectedCard.car_ids || []) {
      const top = (affinity.car_top_drivers?.[cid] || [])[0];
      if (top && !(selectedCard.person_ids || []).includes(top)) out.add(top);
    }
    return out;
  }, [selectedCard, affinity]);

  // Pentru fiecare persoană, calculează unde e alocată în ziua aceasta (din toate carduri + entries)
  const personDayAllocations = useMemo(() => {
    const map = {}; // pid → [{label, color, key, vorarbeiter}]
    for (const c of cards) {
      for (const pid of c.person_ids) {
        (map[pid] = map[pid] || []).push({ label: c.label, color: c.color, key: c.key, vorarbeiter: c.vorarbeiter || null });
      }
    }
    return map;
  }, [cards]);

  const carDayAllocations = useMemo(() => {
    const map = {};
    for (const c of cards) {
      for (const cid of c.car_ids) {
        (map[cid] = map[cid] || []).push({ label: c.label, color: c.color, key: c.key, vorarbeiter: c.vorarbeiter || null });
      }
    }
    return map;
  }, [cards]);

  const allocatedPersonsCount = Object.keys(personDayAllocations).length;
  const allocatedCarsCount = Object.keys(carDayAllocations).length;

  // Quick-add regie state — deschide dialog complet (identic cu cel din /proiecte)
  const [showAddRegie, setShowAddRegie] = useState(false);

  // Edit min/max people pe card — folosește dialogul universal cu scope day/week/forever
  const [editingMinPeople, setEditingMinPeople] = useState(null);

  const onRegieCreated = (nr) => {
    if (!nr || !nr.id) return;
    // Adaugă cardul instant local (apoi onSaved/load() va sincroniza cu backend la închidere)
    setCards((prev) => {
      if (prev.some((c) => c.ref_id === nr.id)) return prev;
      return [...prev, {
        key: `regie-${nr.id}`,
        type: "regie",
        ref_id: nr.id,
        label: nr.name,
        color: nr.color || "#F59E0B",
        is_regie: true,
        firma: nr.firma,
        adresa: nr.adresa,
        note: nr.note,
        entryId: null,
        person_ids: [],
        car_ids: [],
        share_pids: [],
        dirty: false,
      }];
    });
    setSelectedKey(`regie-${nr.id}`);
    toast.success(`Regia "${nr.name}" a fost adăugată. Selectează acum persoanele/mașinile.`);
  };

  // 🧰 Scule: disponibilitate culori (o culoare rămâne blocată pe șantier până la finalizarea lui)
  const [toolLocks, setToolLocks] = useState([]); // [{color, locked_by_label, locked_by_project_id, locked_by_regie_id, released_at}]
  useEffect(() => {
    api.get(`/tool-sets/availability?date_from=${dayIso}&date_to=${dayIso}`)
      .then((r) => setToolLocks(r.data?.locked || []))
      .catch(() => setToolLocks([]));
  }, [dayIso]);

  const setCardProp = (cardKey, prop, value) => {
    setCards((prev) => prev.map((x) => (x.key === cardKey ? { ...x, [prop]: value, dirty: true } : x)));
    queueSave(cardKey);
  };

  // Sugestii scule în funcție de mașinile alocate pe card (mapare predefinită)
  const suggestedToolColors = (c) => {
    const out = new Set();
    for (const cid of (c.car_ids || [])) {
      const car = (cars || []).find((x) => x.id === cid);
      if (!car) continue;
      const mm = `${car.marca || ""} ${car.model || ""}`.toLowerCase();
      const cul = (car.culoare || "").toLowerCase();
      const isTransit = mm.includes("transit");
      const isMaster = mm.includes("master");
      const yellow = /gelb|galben|yellow/.test(cul);
      const white = /weiss|weiß|white|\balb\b/.test(cul);
      const grey = /grau|grey|gray|\bgri\b/.test(cul);
      const black = /schwarz|negru|black/.test(cul);
      if (isTransit && yellow) out.add("galben");
      if (isMaster && white) out.add("rosu");
      if (isMaster && grey) out.add("verde");
      if (isTransit && white) { out.add("albastru"); out.add("mov"); }
      if (isTransit && black) out.add("gri");
    }
    return out;
  };

  // Culoarea e blocată de ALT șantier (nu de cardul curent)
  const toolLockFor = (c, colorCode) => {
    const lock = toolLocks.find((l) => l.color === colorCode);
    if (!lock) return null;
    if ((c.type === "project" && lock.locked_by_project_id === c.ref_id) || (c.type === "regie" && lock.locked_by_regie_id === c.ref_id)) return null;
    return lock;
  };

  const persistCard = async (cardKey) => {
    const c = cardsRef.current.find((x) => x.key === cardKey);
    if (!c) return;
    // Auto-determinăm șoferul pentru fiecare mașină (când există) pe baza
    // persoanelor cu permis_conducere alocate cardului. Dacă există un singur
    // candidat, îl alegem automat. Dacă există mai mulți și nu există deja un
    // șofer desemnat, deschidem un dialog modal pentru a alege.
    const personById = (pid) => (persons || []).find((p) => p.id === pid);
    const driversInTeam = (c.person_ids || [])
      .map(personById)
      .filter(Boolean)
      .filter((p) => p.permis_conducere);
    const carDrivers = { ...(c.car_drivers || {}) };
    for (const carId of (c.car_ids || [])) {
      const existingDriver = carDrivers[carId];
      // Curăță șoferi care nu mai sunt în echipă SAU care nu mai au permis de conducere
      if (existingDriver && (!(c.person_ids || []).includes(existingDriver) || !personById(existingDriver)?.permis_conducere)) {
        delete carDrivers[carId];
      }
      if (!carDrivers[carId]) {
        if (driversInTeam.length === 1) {
          carDrivers[carId] = driversInTeam[0].id;
        } else if (driversInTeam.length > 1) {
          // Cerem alegerea
          const car = (cars || []).find((cc) => cc.id === carId);
          const chosen = await new Promise((resolve) => {
            driverPickResolverRef.current = resolve;
            setDriverPickPending({
              cardKey,
              carId,
              carLabel: car?.label || "(mașină)",
              candidates: driversInTeam.map((p) => ({ id: p.id, name: p.name })),
            });
          });
          if (chosen) carDrivers[carId] = chosen;
        } else if ((c.person_ids || []).length > 0) {
          // niciun șofer dar mașină alocată
          toast.warning(`Atenție: niciun șofer cu permis alocat pentru mașina ${(cars || []).find((cc) => cc.id === carId)?.label || ""}`);
        }
      }
    }
    // Persistăm noua mapare în state local
    setCards((prev) => prev.map((x) => x.key === cardKey ? { ...x, car_drivers: carDrivers } : x));
    const payload = {
      project_id: c.type === "project" ? c.ref_id : null,
      regie_id: c.type === "regie" ? c.ref_id : null,
      label: c.label,
      is_regie: c.is_regie,
      color: c.color,
      day,
      person_ids: c.person_ids,
      car_ids: c.car_ids,
      car_drivers: carDrivers,
      allocation_color: c.allocation_color || null,
      note: c.note || null,
      share_person_ids: c.share_pids || [],
      person_splits: (c.person_splits && c.person_splits[day]) || {},
    };
    setSavingKeys((s) => { const n = new Set(s); n.add(cardKey); return n; });
    try {
      const resp = await api.post("/schedule/upsert-from-day", payload);
      const newId = resp?.data?.id || null;
      setCards((prev) => prev.map((x) => x.key === cardKey ? { ...x, entryId: newId, dirty: false } : x));
      setSavedTickKeys((s) => { const n = new Set(s); n.add(cardKey); return n; });
      setTimeout(() => {
        setSavedTickKeys((s) => { const n = new Set(s); n.delete(cardKey); return n; });
      }, 1500);
      // Notificare dacă s-au făcut realocări (persoane scoase de la alte regii/proiecte)
      const moved = resp?.data?.moved_persons || [];
      if (moved.length > 0) {
        const total = moved.reduce((s, m) => s + (m.removed_count || 0), 0);
        const labels = moved.map((m) => m.subject_label).filter(Boolean).join(", ");
        toast.info(`🔄 ${total} ${total === 1 ? "persoană mutată" : "persoane mutate"} de la: ${labels || "alte alocări"}`);
      }
      // Hint pentru sâmbătă (single-day)
      if (resp?.data?.saturday_only) {
        toast.success(`📅 Alocare sâmbătă — DOAR pe ${payload.day} (nu se propagă)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Eroare la salvarea ${c.label}`);
    } finally {
      setSavingKeys((s) => { const n = new Set(s); n.delete(cardKey); return n; });
    }
  };

  const queueSave = (cardKey) => {
    const timers = saveTimersRef.current;
    if (timers[cardKey]) clearTimeout(timers[cardKey]);
    timers[cardKey] = setTimeout(() => {
      delete timers[cardKey];
      persistCard(cardKey);
    }, 400);
  };

  // La închiderea modalului, asigură că orice debounce pending este flush-uit
  useEffect(() => {
    return () => {
      const timers = saveTimersRef.current;
      Object.entries(timers).forEach(([key, id]) => {
        clearTimeout(id);
        // Salvare imediată pentru orice card cu modificări nesalvate
        const c = cardsRef.current.find((x) => x.key === key);
        if (c && c.dirty) persistCard(key);
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [pendingConflictPid, setPendingConflictPid] = useState(null); // { pid, name, allocs }
  // Pas 2 al conflictului: ore per alocare — { allocTimes: {card_key: {from,to}}, newFrom, newTo }
  const [conflictSplitTimes, setConflictSplitTimes] = useState(null);

  const togglePerson = (pid) => {
    if (!selectedKey) {
      const pers = (persons || []).find((x) => x.id === pid);
      const alreadyAllocated = (personDayAllocations[pid] || []).length > 0;
      if (!alreadyAllocated) {
        api.get(`/planificator/last-worksite?person_id=${pid}`)
          .then((r) => {
            const d = r.data || {};
            if (d.found && d.label) {
              const fmtD = (iso) => (iso ? iso.split("-").reverse().join(".") : "");
              const perioada = d.ongoing ? `din ${fmtD(d.start_date)}` : `${fmtD(d.start_date)}${d.end_date ? ` → ${fmtD(d.end_date)}` : ""}`;
              toast(`💡 ${pers?.name || "Persoana"} a lucrat recent la „${d.label}"`, {
                description: `${d.adresa ? `📍 ${d.adresa} · ` : ""}${perioada}. Selectează un card din stânga pentru a aloca.`,
                duration: 6000,
              });
            } else {
              toast.error("Selectează întâi un proiect sau o regie din stânga");
            }
          })
          .catch(() => toast.error("Selectează întâi un proiect sau o regie din stânga"));
      } else {
        toast.error("Selectează întâi un proiect sau o regie din stânga");
      }
      return;
    }
    const selectedCardLocal = cards.find((c) => c.key === selectedKey);
    const isCurrentlySelected = selectedCardLocal && selectedCardLocal.person_ids.includes(pid);
    if (isCurrentlySelected) {
      // Deselectare normală — scoate din person_ids și din share_pids (dacă era marcată split-shift)
      setCards((prev) => prev.map((c) => c.key === selectedKey ? {
        ...c,
        person_ids: c.person_ids.filter((x) => x !== pid),
        share_pids: (c.share_pids || []).filter((x) => x !== pid),
        dirty: true,
      } : c));
      queueSave(selectedKey);
      return;
    }
    // Selectare: dacă persoana e deja alocată la alt card pe ACEEAȘI zi → întreabă
    const allocs = personDayAllocations[pid] || [];
    const conflicts = allocs.filter((a) => a.card_key !== selectedKey);
    if (conflicts.length > 0) {
      const pers = (persons || []).find((p) => p.id === pid);
      setPendingConflictPid({ pid, name: pers?.name || "—", allocs: conflicts });
      return;
    }
    setCards((prev) => prev.map((c) => c.key === selectedKey ? { ...c, person_ids: [...c.person_ids, pid], dirty: true } : c));
    queueSave(selectedKey);
  };

  const acceptConflictBoth = () => {
    if (!pendingConflictPid || !selectedKey) { setPendingConflictPid(null); return; }
    const { pid } = pendingConflictPid;
    // Adăugăm persoana pe cardul curent ȘI o marcăm ca "share" (backend NU o va scoate de la alte alocări)
    setCards((prev) => prev.map((c) => c.key === selectedKey ? {
      ...c,
      person_ids: c.person_ids.includes(pid) ? c.person_ids : [...c.person_ids, pid],
      share_pids: (c.share_pids || []).includes(pid) ? (c.share_pids || []) : [...(c.share_pids || []), pid],
      dirty: true,
    } : c));
    queueSave(selectedKey);
    toast.success(`${pendingConflictPid.name} lucrează la ambele alocări (split). Notează orele în „Orele mele".`);
    setPendingConflictPid(null);
    setConflictSplitTimes(null);
  };

  // Split orar cu ore explicite: setează intervalul pe alocarea existentă + pe cardul curent
  const acceptConflictBothWithTimes = async () => {
    if (!pendingConflictPid || !selectedKey || !conflictSplitTimes) return;
    const { pid, allocs } = pendingConflictPid;
    const t = conflictSplitTimes;
    // 1. Cardul curent: adaugă persoana + share + split orar local
    setCards((prev) => prev.map((c) => {
      if (c.key === selectedKey) {
        const daySplits = { ...((c.person_splits || {})[day] || {}), [pid]: { from: t.newFrom, to: t.newTo } };
        return {
          ...c,
          person_ids: c.person_ids.includes(pid) ? c.person_ids : [...c.person_ids, pid],
          share_pids: (c.share_pids || []).includes(pid) ? (c.share_pids || []) : [...(c.share_pids || []), pid],
          person_splits: { ...(c.person_splits || {}), [day]: daySplits },
          dirty: true,
        };
      }
      const at = t.allocTimes[c.key];
      if (at) {
        const daySplits = { ...((c.person_splits || {})[day] || {}), [pid]: { from: at.from, to: at.to } };
        return { ...c, person_splits: { ...(c.person_splits || {}), [day]: daySplits } };
      }
      return c;
    }));
    queueSave(selectedKey);
    // 2. Alocările existente: salvează intervalul direct pe entry (PATCH)
    for (const a of allocs) {
      const at = t.allocTimes[a.card_key];
      const entryCard = cardsRef.current.find((c) => c.key === a.card_key);
      const eid = entryCard?.entryId;
      if (at && eid) {
        try {
          await api.patch(`/schedule/${eid}/person-split`, { date: day, person_id: pid, from_time: at.from, to_time: at.to });
        } catch (err) {
          toast.error(`Nu am putut salva orele pe „${a.label}"`);
        }
      }
    }
    toast.success(`${pendingConflictPid.name}: split orar salvat (${Object.values(t.allocTimes).map((x) => `${x.from}–${x.to}`).join(", ")} apoi ${t.newFrom}–${t.newTo}).`);
    setPendingConflictPid(null);
    setConflictSplitTimes(null);
  };
  const acceptConflictMove = () => {
    if (!pendingConflictPid || !selectedKey) { setPendingConflictPid(null); return; }
    const { pid, allocs } = pendingConflictPid;
    const conflictKeys = new Set(allocs.map((a) => a.card_key));
    setCards((prev) => prev.map((c) => {
      if (c.key === selectedKey) {
        return { ...c, person_ids: c.person_ids.includes(pid) ? c.person_ids : [...c.person_ids, pid], dirty: true };
      }
      if (conflictKeys.has(c.key)) {
        return { ...c, person_ids: c.person_ids.filter((x) => x !== pid), dirty: true };
      }
      return c;
    }));
    // Salvează ambele cards
    queueSave(selectedKey);
    conflictKeys.forEach((k) => queueSave(k));
    toast.success(`${pendingConflictPid.name} a fost mutat(ă) din ${conflictKeys.size} alocare${conflictKeys.size === 1 ? "" : "i"} anterioară.`);
    setPendingConflictPid(null);
  };

  const toggleCar = (cid) => {
    if (!selectedKey) {
      toast.error("Selectează întâi un proiect sau o regie din stânga");
      return;
    }
    const card = cards.find((c) => c.key === selectedKey);
    const adding = card && !(card.car_ids || []).includes(cid);
    setCards((prev) => prev.map((c) => {
      if (c.key !== selectedKey) return c;
      const has = c.car_ids.includes(cid);
      return { ...c, car_ids: has ? c.car_ids.filter((x) => x !== cid) : [...c.car_ids, cid], dirty: true };
    }));
    queueSave(selectedKey);
    // ⚠️ Meldung: sculele din dubă sunt încă pe alt șantier neterminat?
    if (adding && card) {
      const params = { car_id: cid };
      if (card.type === "project") params.target_project_id = card.ref_id;
      if (card.type === "regie") params.target_regie_id = card.ref_id;
      api.get("/tool-sets/car-conflict", { params }).then((r) => {
        if (r.data?.conflict) {
          for (const cf of r.data.conflicts) {
            toast.warning(
              `⚠️ ATENȚIE: sculele ${cf.color.toUpperCase()} din această dubă sunt încă alocate pe „${cf.site}" (neterminat, până la ${cf.until}). Cum aloci sculele astea dacă șantierul unde sunt nu e terminat?`,
              { duration: 12000 }
            );
          }
        }
      }).catch(() => {});
    }
  };

  const removeFromCard = (cardKey, pid) => {
    setCards((prev) => prev.map((c) => c.key === cardKey ? {
      ...c,
      person_ids: c.person_ids.filter((x) => x !== pid),
      share_pids: (c.share_pids || []).filter((x) => x !== pid),
      dirty: true,
    } : c));
    queueSave(cardKey);
  };
  const removeCarFromCard = (cardKey, cid) => {
    setCards((prev) => prev.map((c) => c.key === cardKey ? { ...c, car_ids: c.car_ids.filter((x) => x !== cid), dirty: true } : c));
    queueSave(cardKey);
  };

  const isOnLeaveDay = (p) => (p.vacations || []).some((v) => (v.start_date || "") <= day && day <= (v.end_date || ""));
  const basePersons = (persons || [])
    .filter((p) => !p.is_admin_user)  // ascunde adminii
    .filter((p) => !qp || p.name.toLowerCase().includes(qp.toLowerCase()));
  // Disponibili (fără concediu/indisponibilitate pe ziua selectată)
  const filteredPersons = basePersons.filter((p) => !isOnLeaveDay(p));
  // În concediu/indisponibili — afișați separat (vizibili la căutare, dar neselectabili)
  // Split per day-availability: free azi (fără alocări pe ziua curentă) sus, ocupate jos
  const isPersonFreeToday = (p) => {
    const allocs = personDayAllocations[p.id] || [];
    return allocs.length === 0;
  };
  const isDismantleSelected = selectedCard && selectedCard.is_dismantle === true;
  // Prioritate vizuală: șoferi (cu permis) primii, apoi EKAS, apoi restul.
  const personSortPriority = (p) => {
    if (p.permis_conducere) return 0;
    if (p.ekas) return 1;
    return 2;
  };
  const sortByRole = (arr) => [...arr].sort((a, b) => {
    const da = personSortPriority(a) - personSortPriority(b);
    if (da !== 0) return da;
    return (a.name || "").localeCompare(b.name || "", "ro");
  });
  const promoteSuggestedDrivers = (arr) => [...arr].sort((a, b) => (suggestedDriverIds.has(b.id) ? 1 : 0) - (suggestedDriverIds.has(a.id) ? 1 : 0));

  // 📦 Alocare rapidă la MAGAZIE (proiectul special is_magazia)
  const magaziaCardKey = useMemo(() => {
    const mp = (projects || []).find((p) => p.is_magazia);
    return mp ? `proj-${mp.id}` : null;
  }, [projects]);
  const magaziaHasPerson = (pid) => {
    if (!magaziaCardKey) return false;
    const mc = cards.find((c) => c.key === magaziaCardKey);
    return !!mc && (mc.person_ids || []).includes(pid);
  };
  const sendToMagazie = (p) => {
    if (!magaziaCardKey) { toast.error("Proiectul MAGAZIA nu există"); return; }
    setCards((prev) => prev.map((c) => c.key === magaziaCardKey && !(c.person_ids || []).includes(p.id)
      ? { ...c, person_ids: [...c.person_ids, p.id], dirty: true }
      : c));
    queueSave(magaziaCardKey);
    toast.success(`📦 ${p.name} alocat la MAGAZIE pe ${day}`);
  };
  const personsFreeToday = promoteSuggestedDrivers(isDismantleSelected ? sortByRole(filteredPersons) : sortByRole(filteredPersons.filter(isPersonFreeToday)));
  const personsBusyToday = isDismantleSelected ? [] : sortByRole(filteredPersons.filter((p) => !isPersonFreeToday(p)));
  const personsOnLeaveToday = sortByRole(basePersons.filter((p) => isOnLeaveDay(p) && !(personDayAllocations[p.id] || []).length));

  const renderPersonRow = (p) => {
    const allocs = personDayAllocations[p.id] || [];
    const inSelected = selectedCard && selectedCard.person_ids.includes(p.id);
    const dayVac = (p.vacations || []).find((v) => (v.start_date || "") <= day && day <= (v.end_date || ""));
    const isOnLeave = !!dayVac;
    const leaveLabel = dayVac?.kind === "unavailable" ? "Indisponibil" : "Concediu";
    const leaveIcon = dayVac?.kind === "unavailable" ? "🚫" : "🏖";
    const specialLabel = p.special_label || (p.is_admin_user ? "Admin" : null);
    const isSuggestedDriver = suggestedDriverIds.has(p.id) && !inSelected && !isOnLeave;
    const isPastWorker = pastWorkers[p.id] != null && !inSelected && !isOnLeave;
    return (
      <div
        key={p.id}
        className={`w-full flex items-stretch gap-1 rounded border text-sm transition-colors ${
          isOnLeave ? (dayVac?.kind === "unavailable" ? "bg-amber-50 border-amber-300 opacity-75" : "bg-blue-50 border-blue-300 opacity-75")
          : specialLabel ? "bg-purple-50 border-purple-300 opacity-90"
          : inSelected ? "bg-emerald-50 border-emerald-400 font-semibold"
          : isSuggestedDriver ? "pulse-green-suggest"
          : isPastWorker ? "pulse-amber-history"
          : "border-zinc-200 hover:bg-zinc-50"
        }`}
      >
        <button
          type="button"
          onClick={() => togglePerson(p.id)}
          disabled={isOnLeave}
          className={`flex-1 flex items-start gap-2 px-2 py-1.5 text-left min-w-0 ${isOnLeave ? "cursor-not-allowed" : ""}`}
          data-testid={`mass-pick-person-${p.id}`}
        >
          <span className={`w-4 h-4 rounded border ${inSelected ? "bg-emerald-600 border-emerald-600 text-white" : "border-zinc-300"} grid place-items-center text-[10px] flex-shrink-0 mt-0.5`}>
            {inSelected ? "✓" : ""}
          </span>
          <span className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="truncate">{p.name}</span>
              {p.limba_germana && <span className="flex-shrink-0" title="Vorbește limba germană" data-testid={`person-german-${p.id}`}>🇩🇪</span>}
              {p.cazare_city && <span className="text-[10px] text-zinc-500 flex-shrink-0" title={`Cazat în ${p.cazare_city}`} data-testid={`person-city-${p.id}`}>({p.cazare_city})</span>}
              {p.ekas && <span className="text-yellow-500 text-[12px]" title="EKAS" data-testid={`mass-ekas-${p.id}`}>⭐</span>}
              {p.permis_conducere && <span className="text-blue-600 text-[12px]" title="Permis de conducere" data-testid={`mass-license-${p.id}`}>🚗</span>}
              {suggestedDriverIds.has(p.id) && !inSelected && !isOnLeave && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900" data-testid={`suggested-driver-${p.id}`}>
                  🚗 șoferul mașinii alocate
                </span>
              )}
              {isPastWorker && (
                <span
                  className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-200 text-amber-900"
                  title={`A lucrat deja aici (${pastWorkers[p.id]} ore/alocări)`}
                  data-testid={`past-worker-${p.id}`}
                >
                  🔁 a lucrat aici
                </span>
              )}
              {isOnLeave && (
                <span
                  className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${dayVac?.kind === "unavailable" ? "bg-amber-200 text-amber-900" : "bg-blue-200 text-blue-900"}`}
                  title={dayVac?.note || ""}
                  data-testid={`person-leave-${p.id}`}
                >
                  {leaveIcon} {leaveLabel}
                </span>
              )}
              {!isOnLeave && specialLabel && (
                <span
                  className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-200 text-purple-900"
                  data-testid={`person-special-${p.id}`}
                >
                  ⭐ {specialLabel}
                </span>
              )}
            </div>
            {allocs.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-0.5">
                {allocs.map((a) => (
                  <span
                    key={`${p.id}-${a.key}`}
                    className="text-[9px] px-1.5 py-0.5 rounded text-white"
                    style={{ background: a.color }}
                    title={a.vorarbeiter ? `Vorarbeiter: ${a.vorarbeiter}` : undefined}
                  >
                    {a.label}
                    {a.vorarbeiter && (
                      <span className="ml-1 px-1 py-0.5 rounded bg-white/25 font-semibold tracking-tight">
                        👷 {a.vorarbeiter}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </span>
        </button>
        {!isOnLeave && !p.is_admin_user && !magaziaHasPerson(p.id) && (
          <button
            type="button"
            onClick={(ev) => { ev.stopPropagation(); sendToMagazie(p); }}
            className="flex-shrink-0 self-center text-[9px] font-bold uppercase tracking-wider px-1.5 py-1 rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors"
            title="Alocă la MAGAZIE pe această zi"
            data-testid={`send-magazie-${p.id}`}
          >
            📦 Magazie
          </button>
        )}
        <PersonAvailabilityMenu person={p} day={day} onSaved={onSaved} />
      </div>
    );
  };
  // Tip vehicul: explicit sau euristic (dube primele în listă)
  const isDuba = (c) => {
    if (c.vehicle_type) return c.vehicle_type === "duba";
    return /transit|master|jumpy|crafter|sprinter|ducato|boxer|vito|transporter|expert|scudo|trafic/i.test(`${c.marca || ""} ${c.model || ""}`) && (c.nr_locuri || 0) < 8;
  };
  const filteredCars = (cars || []).filter((c) => !qc || c.label.toLowerCase().includes(qc.toLowerCase()))
    .sort((a, b) =>
      (suggestedCarIds.has(b.id) ? 2 : 0) + (isDuba(b) ? 1 : 0)
      - ((suggestedCarIds.has(a.id) ? 2 : 0) + (isDuba(a) ? 1 : 0))
    );

  const personById = (id) => (persons || []).find((p) => p.id === id);
  const carById = (id) => (cars || []).find((c) => c.id === id);

  const [statusBusyKey, setStatusBusyKey] = useState(null);
  const [dismantleTarget, setDismantleTarget] = useState(null); // {cardKey, refId, label} pentru dialogul Demontat
  const [pauseTarget, setPauseTarget] = useState(null); // {cardKey, refId, label} pentru dialog pauză cu interval
  const handleRegieAction = async (cardKey, refId, action, label) => {
    if (statusBusyKey) return;
    // Pentru "pause", deschidem dialog separat cu date picker (interval pauză)
    if (action === "pause") {
      setPauseTarget({ cardKey, refId, label });
      return;
    }
    const confirmText = action === "close"
      ? `Sigur termini regia "${label}"? Toate alocările viitoare vor fi eliberate.`
      : null;
    if (confirmText && !window.confirm(confirmText)) return;
    setStatusBusyKey(cardKey);
    try {
      await api.patch(`/regii/${refId}/status`, { action });
      toast.success(action === "resume" ? `Regie reluată` : `Regie terminată`);
      // Pentru "close" — scoatem cardul DIN LOCAL STATE imediat ca să nu mai apară în lista de alocare.
      // (`(onRefresh || onSaved)()` actualizează propsele dar `cards` se sincronizează DOAR pe schimbarea zilei.)
      if (action === "close") {
        setCards((prev) => prev.filter((c) => c.key !== cardKey));
        if (selectedKey === cardKey) setSelectedKey(null);
      }
      // Refresh DOAR datele, fără să închidem dialogul (utilizatorul rămâne în Planificator)
      (onRefresh || onSaved)();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la modificare status");
    } finally { setStatusBusyKey(null); }
  };

  const submitPause = async ({ start_date, end_date, note }) => {
    if (!pauseTarget) return;
    setStatusBusyKey(pauseTarget.cardKey);
    try {
      await api.patch(`/regii/${pauseTarget.refId}/status`, {
        action: "pause", start_date, end_date: end_date || null, note: note || null,
      });
      toast.success(`Regie pusă în pauză ${start_date}${end_date ? ` → ${end_date}` : " (nedefinit)"}`);
      setPauseTarget(null);
      (onRefresh || onSaved)();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setStatusBusyKey(null); }
  };
  const handleProjectStatus = async (cardKey, refId, newStatus, label) => {
    if (statusBusyKey) return;
    let confirmText;
    if (newStatus === "Demontat") {
      confirmText = `Marchezi proiectul "${label}" ca DEMONTAT? Toate alocările viitoare vor fi eliberate.`;
    } else if (newStatus === "În lucru" || newStatus === "In lucru") {
      confirmText = `Reia proiectul "${label}" (îl muți înapoi în categoria 6 — În lucru)?`;
    } else {
      confirmText = `Marchezi proiectul "${label}" ca FINALIZAT — DE DEMONTAT?`;
    }
    if (!window.confirm(confirmText)) return;
    setStatusBusyKey(cardKey);
    try {
      await api.patch(`/projects/${refId}/status`, { status: newStatus });
      toast.success(`Status setat: ${newStatus}`);
      // Refresh DOAR datele, fără să închidem dialogul (utilizatorul rămâne în Planificator)
      (onRefresh || onSaved)();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la modificare status");
    } finally { setStatusBusyKey(null); }
  };
  const submitDismantle = async ({ personIds, carIds, note, dismantleDate, brought_back, left_on_site }) => {
    if (!dismantleTarget) return;
    setStatusBusyKey(dismantleTarget.cardKey);
    try {
      const bb = Array.isArray(brought_back) ? brought_back : [];
      const los = Array.isArray(left_on_site) ? left_on_site : [];
      if (bb.length > 0 || los.length > 0) {
        await api.post("/inventory/site-leftovers/dismantle-batch", {
          project_id: dismantleTarget.refId,
          brought_back: bb,
          left_on_site: los,
          note: note || null,
        });
      }
      await api.patch(`/projects/${dismantleTarget.refId}/status`, {
        status: "Demontat",
        dismantled_by_person_ids: personIds,
        dismantled_by_car_ids: carIds,
        dismantled_note: note || null,
        dismantle_date: dismantleDate || null,
      });
      toast.success(`"${dismantleTarget.label}" marcat ca DEMONTAT pe ${dismantleDate || "azi"}. Dispare din Planificator.`);
      // Scoatem cardul din state local imediat — dacă ziua curentă != dismantleDate, proiectul nu mai e eligibil pentru ea.
      const targetKey = dismantleTarget.cardKey;
      if (!dismantleDate || dismantleDate !== day) {
        setCards((prev) => prev.filter((c) => c.key !== targetKey));
        if (selectedKey === targetKey) setSelectedKey(null);
      }
      setDismantleTarget(null);
      (onRefresh || onSaved)();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la marcarea ca demontat");
    } finally { setStatusBusyKey(null); }
  };

  const renderCard = (c) => {
    const isSelected = c.key === selectedKey;
    const minP = Number(c.min_people || 0);
    const minC = Number(c.min_cars || 0);
    const haveP = c.person_ids.length;
    const haveC = c.car_ids.length;
    const peopleOk = minP === 0 || haveP >= minP;
    const carsOk = minC === 0 || haveC >= minC;
    const allOk = peopleOk && carsOk && (minP > 0 || minC > 0);
    const partial = (minP > 0 || minC > 0) && (haveP > 0 || haveC > 0) && !allOk;
    // 🧰 Sculele alocate colorează fereastra șantierului/regiei în culoarea lor
    const toolDef = c.allocation_color ? INVENTORY_SET_COLORS.find((t) => t.code === c.allocation_color) : null;
    return (
      <button
        key={c.key}
        type="button"
        onClick={() => setSelectedKey(c.key)}
        className={`text-left rounded-lg border-2 p-3 w-full transition-all ${isSelected ? "ring-4 ring-offset-1 shadow-md scale-[1.01]" : "hover:shadow"}`}
        style={{
          borderColor: toolDef ? toolDef.hex : (allOk ? "#10b981" : c.color),
          background: toolDef ? `${toolDef.hex}22` : (allOk ? "#ecfdf5" : (isSelected ? `${c.color}15` : "white")),
          boxShadow: toolDef ? `inset 4px 0 0 0 ${toolDef.hex}` : undefined,
        }}
        data-testid={`card-${c.key}`}
      >
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-base leading-tight flex items-center gap-1.5 flex-wrap" style={{ color: toolDef ? toolDef.hex : (allOk ? "#059669" : c.color) }}>
              <span>{c.label}</span>
              {toolDef && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: toolDef.hex }} data-testid={`card-tool-badge-${c.key}`} title={`Scule ${toolDef.label.toLowerCase()} pe acest șantier (rămân până la finalizare)`}>
                  🧰 {toolDef.label.toUpperCase()}
                </span>
              )}
              {c.ref_id && (c.type === "project" || c.type === "regie") && (
                <>
                  <QuickEditPopover
                    card={c}
                    onSaved={onRefresh || onSaved}
                    onCardUpdated={(key, partial) => setCards((prev) => prev.map((x) => x.key === key ? { ...x, ...partial } : x))}
                    regii={regii}
                  />
                  <span
                    role="button"
                    tabIndex={0}
                    title={c.type === "regie" ? "Deschide pagina regiei (modifică detalii)" : "Deschide pagina proiectului (modifică detalii)"}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const url = c.type === "regie" ? `/proiecte?regie=${c.ref_id}&edit=1` : `/proiecte?project=${c.ref_id}&edit=1`;
                      window.open(url, "_blank", "noopener,noreferrer");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        e.preventDefault();
                        const url = c.type === "regie" ? `/proiecte?regie=${c.ref_id}&edit=1` : `/proiecte?project=${c.ref_id}&edit=1`;
                        window.open(url, "_blank", "noopener,noreferrer");
                      }
                    }}
                    className="inline-flex items-center justify-center w-6 h-6 rounded text-[var(--primary)] bg-[var(--primary-subtle)] hover:bg-[var(--primary)] hover:text-white transition-colors cursor-pointer"
                    data-testid={`open-detail-${c.key}`}
                  >
                    ↗
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    title="Istoric — ce s-a întâmplat în trecut (ore, persoane, mașini)"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setHistoryFor({ type: c.type, ref_id: c.ref_id, label: c.label });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        e.preventDefault();
                        setHistoryFor({ type: c.type, ref_id: c.ref_id, label: c.label });
                      }
                    }}
                    className="inline-flex items-center justify-center gap-0.5 h-6 px-1.5 rounded text-[10px] font-bold text-amber-800 bg-amber-100 hover:bg-amber-500 hover:text-white transition-colors cursor-pointer"
                    data-testid={`history-${c.key}`}
                  >
                    🕘 Istoric
                  </span>
                </>
              )}
              {allOk && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded align-middle">✓ Complet</span>}
            </div>
            <div
              className={`flex items-center gap-2 mt-1 flex-wrap ${c.ref_id && (c.type === "regie" || c.type === "project") ? "cursor-pointer hover:bg-zinc-50 rounded -mx-1 px-1 py-0.5 transition-colors" : ""}`}
              role={c.ref_id && (c.type === "regie" || c.type === "project") ? "button" : undefined}
              tabIndex={c.ref_id && (c.type === "regie" || c.type === "project") ? 0 : undefined}
              title={c.ref_id && (c.type === "regie" || c.type === "project") ? "Click pe oricare badge pentru a modifica detalii (firma, adresă, date, persoane)" : undefined}
              onClick={(e) => {
                if (!c.ref_id || (c.type !== "regie" && c.type !== "project")) return;
                e.stopPropagation();
                e.preventDefault();
                // Click pe orice badge → simulează click pe ⚡ (deschide popover-ul de editare rapidă)
                const trigger = e.currentTarget.parentElement.parentElement.querySelector(`[data-testid="quick-edit-${c.key}"]`);
                if (trigger) trigger.click();
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                if (!c.ref_id || (c.type !== "regie" && c.type !== "project")) return;
                e.stopPropagation();
                e.preventDefault();
                const trigger = e.currentTarget.parentElement.parentElement.querySelector(`[data-testid="quick-edit-${c.key}"]`);
                if (trigger) trigger.click();
              }}
              data-testid={`card-${c.key}-meta-row`}
            >
              {c.is_regie && <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">REGIE</span>}
              {c.firma && <span className="text-[10px] text-zinc-600">🏢 {c.firma}</span>}
              {c.adresa && <span className="text-[10px] text-zinc-600">📍 {c.adresa}</span>}
              {c.vorarbeiter && (
                <span
                  className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                  title={`Vorarbeiter (șef de echipă): ${c.vorarbeiter}`}
                  data-testid={`card-${c.key}-vorarbeiter`}
                >
                  👷 {c.vorarbeiter}
                </span>
              )}
              {c.category && <span className="text-[9px] font-semibold bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded">cat. {c.category}</span>}
              {c.is_end_past && !c.is_dismantled && (
                <span
                  className="text-[10px] font-extrabold uppercase tracking-wider bg-orange-600 text-white px-2 py-0.5 rounded animate-pulse"
                  title={`Proiectul s-a încheiat pe ${fmtRoDate(c.end_date)}. Astăzi e doar pentru demontaj.`}
                  data-testid={`card-${c.key}-end-past`}
                >
                  🔧 ÎNCHEIAT — DOAR DEMONTAJ
                </span>
              )}
              {c.is_dismantled && (
                <span
                  className="text-[10px] font-extrabold uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded"
                  title={`Demontat pe ${fmtRoDate(c.dismantle_date)} de ${c.dismantled_by_user || "—"}`}
                  data-testid={`card-${c.key}-dismantled`}
                >
                  ✅ DEMONTAT · {fmtRoDate(c.dismantle_date) || "?"}
                </span>
              )}
              {c.type === "project" && (c.start_date || c.end_date) && (
                <span
                  className="text-[10px] font-semibold bg-indigo-50 text-indigo-800 border border-indigo-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                  title="Perioada planificată a proiectului (start – end)"
                  data-testid={`card-${c.key}-period`}
                >
                  📅 {fmtRoDate(c.start_date) || "?"} – {fmtRoDate(c.end_date) || "?"}
                </span>
              )}
              {c.type === "regie" && (c.start_date || c.end_date || c.indeterminate_end) && (
                <span
                  className="text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                  title="Perioada în care regia se lucrează"
                  data-testid={`card-${c.key}-period`}
                >
                  📅 {fmtRoDate(c.start_date) || "?"} – {c.indeterminate_end ? "indeterminat" : (fmtRoDate(c.end_date) || "?")}
                </span>
              )}
              {c.type === "regie" && Array.isArray(c.pauses) && c.pauses.length > 0 && (
                <span
                  className="text-[10px] font-semibold bg-purple-50 text-purple-800 border border-purple-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                  title={c.pauses.map((p) => `${fmtRoDate(p.start_date)} → ${fmtRoDate(p.end_date) || "..."}`).join("; ")}
                  data-testid={`card-${c.key}-pauses`}
                >
                  ⏸ {c.pauses.length} {c.pauses.length === 1 ? "pauză" : "pauze"}
                </span>
              )}
            </div>
            {/* Detalii demontare (când proiectul e demontat) */}
            {c.is_dismantled && (
              <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-[11px] space-y-0.5" data-testid={`card-${c.key}-dismantle-info`}>
                <div className="flex items-center gap-1.5">
                  <span className="text-emerald-700 font-bold">✅ Demontat:</span>
                  <span><strong>{fmtRoDate(c.dismantle_date) || "?"}</strong> de către <strong>{c.dismantled_by_user || "—"}</strong></span>
                </div>
                {(c.dismantled_by_person_ids || []).length > 0 && (
                  <div>
                    <span className="text-emerald-700 font-bold">👥 Echipa:</span>{" "}
                    {(c.dismantled_by_person_ids || []).map((pid) => persons.find((p) => p.id === pid)?.nume_full || pid).join(", ")}
                  </div>
                )}
                {c.dismantled_note && (
                  <div className="italic text-zinc-600">📝 {c.dismantled_note}</div>
                )}
              </div>
            )}
            {c.note && (
              <div
                className="mt-2 px-2 py-1 rounded bg-yellow-50 border border-yellow-300 text-[11px] italic text-yellow-900 font-medium"
                title={c.note}
                data-testid={`card-${c.key}-note-display`}
              >
                📝 {c.note}
              </div>
            )}
            {/* Min requirements indicator */}
            {(minP > 0 || minC > 0 || c.ref_id) && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {(c.ref_id && (c.type === "regie" || c.type === "project")) ? (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Deschide dialogul universal de modificare necesar (cu opțiuni scope: day/week/forever)
                      setEditingMinPeople({
                        target: { kind: c.type, id: c.ref_id, name: c.label },
                        isoDay: day,
                        currentMin: minP,
                        currentMax: Number(c.max_people || 0),
                      });
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.currentTarget.click(); } }}
                    className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded cursor-pointer hover:ring-2 hover:ring-amber-400 ${peopleOk ? "bg-emerald-100 text-emerald-800" : (haveP > 0 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700")}`}
                    data-testid={`card-${c.key}-min-people`}
                    title="Click pentru a modifica necesarul (zi / săptămână / de azi în colo)"
                  >
                    👥 {haveP}/{minP}{c.max_people && c.max_people > minP ? `–${c.max_people}` : ""} {peopleOk ? "✓" : ""} <span className="text-[9px] opacity-70">✎</span>
                  </span>
                ) : minP > 0 && (
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded ${peopleOk ? "bg-emerald-100 text-emerald-800" : (haveP > 0 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700")}`}
                    data-testid={`card-${c.key}-min-people`}
                  >
                    👥 {haveP}/{minP}{c.max_people && c.max_people > minP ? `–${c.max_people}` : ""} {peopleOk ? "✓" : ""}
                  </span>
                )}
                {minC > 0 && (
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded ${carsOk ? "bg-emerald-100 text-emerald-800" : (haveC > 0 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700")}`}
                    data-testid={`card-${c.key}-min-cars`}
                  >
                    🚗 {haveC}/{minC} {carsOk ? "✓" : ""}
                  </span>
                )}
                {partial && <span className="text-[9px] uppercase font-bold tracking-wider text-amber-700">incomplet</span>}
              </div>
            )}
            {c.description && <div className="text-[11px] text-zinc-600 mt-1 line-clamp-2">{c.description}</div>}
            {c.work_zones && <div className="text-[11px] text-zinc-600 mt-0.5"><strong>Zone:</strong> {c.work_zones}</div>}
            {c.special && <div className="text-[11px] text-amber-700 mt-0.5">⚠ {c.special}</div>}
            {c.note && <div className="text-[11px] text-zinc-500 italic mt-0.5">{c.note}</div>}
          </div>
          {savingKeys.has(c.key) && (
            <span className="text-[9px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded whitespace-nowrap inline-flex items-center gap-1" data-testid={`card-saving-${c.key}`}>
              <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"/> salvare...
            </span>
          )}
          {!savingKeys.has(c.key) && savedTickKeys.has(c.key) && (
            <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded whitespace-nowrap" data-testid={`card-saved-${c.key}`}>✓ salvat</span>
          )}
        </div>
        <div className="flex items-start gap-2 pt-2 border-t" style={{ borderColor: `${c.color}40` }}>
          <div className="flex-1">
            <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 mb-1">👷 Persoane ({c.person_ids.length})</div>
            <div className="flex flex-wrap gap-1">
              {c.person_ids.length === 0 && <span className="text-[10px] text-zinc-400 italic">Niciuna</span>}
              {c.person_ids.map((pid) => {
                const p = personById(pid);
                const isShared = (c.share_pids || []).includes(pid);
                const split = c.person_splits?.[day]?.[pid];
                return (
                  <span
                    key={pid}
                    onClick={(ev) => { ev.stopPropagation(); removeFromCard(c.key, pid); }}
                    className={`inline-flex items-center gap-1 text-[10px] border rounded-full px-2 py-0.5 hover:bg-red-50 hover:border-red-300 cursor-pointer ${isShared || split ? "bg-blue-50 border-blue-300 text-blue-800" : "bg-white"}`}
                    data-testid={`card-${c.key}-person-${pid}`}
                    title={split ? `Split orar: ${split.from || "?"} – ${split.to || "?"}` : (isShared ? "Split-shift: lucrează și la alte alocări în această zi" : undefined)}
                  >
                    {(isShared || split) && <span className="text-blue-600">⏱</span>}
                    {p?.name || "—"}
                    {p?.limba_germana && <span title="Vorbește limba germană" data-testid={`card-german-${pid}`}>🇩🇪</span>}
                    {p?.cazare_city && <span className="text-[9px] text-zinc-500" title={`Cazat în ${p.cazare_city}`} data-testid={`card-city-${pid}`}>({p.cazare_city})</span>}
                    {split && <span className="font-bold text-blue-700">{split.from}–{split.to}</span>}
                    {p?.ekas && <span className="text-yellow-500" title="EKAS">⭐</span>}
                    {p?.permis_conducere && <span className="text-blue-600" title="Permis de conducere">🚗</span>}
                    <span className="text-red-500 font-bold">×</span>
                  </span>
                );
              })}
            </div>
          </div>
          <div className="flex-1">
            <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 mb-1">🚗 Mașini ({c.car_ids.length})</div>
            <div className="flex flex-wrap gap-1">
              {c.car_ids.length === 0 && <span className="text-[10px] text-zinc-400 italic">Niciuna</span>}
              {c.car_ids.map((cid) => {
                const carItem = carById(cid);
                const driverId = (c.car_drivers || {})[cid];
                const driver = driverId ? personById(driverId) : null;
                return (
                  <span
                    key={cid}
                    onClick={(ev) => { ev.stopPropagation(); removeCarFromCard(c.key, cid); }}
                    className="inline-flex items-center gap-1 text-[10px] bg-white border rounded-full px-2 py-0.5 hover:bg-red-50 hover:border-red-300 cursor-pointer"
                    data-testid={`card-${c.key}-car-${cid}`}
                    title={driver ? `Șofer: ${driver.name}` : "Nu există șofer desemnat"}
                  >
                    {carItem?.label || "—"}
                    {driver && <span className="text-blue-600" title={`Șofer: ${driver.name}`}>🚗 {driver.name.split(" ")[0]}</span>}
                    <span className="text-red-500 font-bold">×</span>
                  </span>
                );
              })}
            </div>
          </div>
        </div>
        {/* 🧰 Scule (set colorat) — sugestie pulsantă în funcție de mașina alocată */}
        {(() => { const needsTools = ((c.person_ids || []).length > 0 || (c.car_ids || []).length > 0) && !c.allocation_color; return (
        <div
          onClick={(ev) => ev.stopPropagation()}
          className={`mt-2 flex items-center gap-1.5 flex-wrap rounded-md ${needsTools ? "border-2 border-red-500 bg-red-50 p-1.5 pulse-amber-history" : ""}`}
          data-testid={needsTools ? `tools-missing-${c.key}` : undefined}
        >
          <span className={`text-[9px] font-bold uppercase tracking-wider ${needsTools ? "text-red-600 animate-pulse" : "text-zinc-500"}`}>
            {needsTools ? "⚠ 🧰 ALOCĂ SCULELE!" : "🧰 Scule:"}
          </span>
          {INVENTORY_SET_COLORS.map((tc) => {
            const selected = c.allocation_color === tc.code;
            const lock = toolLockFor(c, tc.code);
            const suggested = !selected && !lock && suggestedToolColors(c).has(tc.code);
            return (
              <button
                key={tc.code}
                type="button"
                disabled={!!lock}
                onClick={(e) => { e.stopPropagation(); if (lock) return; setCardProp(c.key, "allocation_color", selected ? null : tc.code); }}
                className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border transition-colors ${selected ? "text-white" : lock ? "bg-zinc-100 text-zinc-400 cursor-not-allowed line-through" : "bg-white text-zinc-700 hover:border-zinc-500"} ${suggested ? "pulse-amber-history" : ""}`}
                style={selected ? { background: tc.hex, borderColor: tc.hex } : { borderColor: suggested ? tc.hex : "#d4d4d8" }}
                title={lock ? `Ocupate la „${lock.locked_by_label || "alt șantier"}" până la ${lock.released_at || "finalizare"} — sculele rămân pe șantier până se termină` : suggested ? `Sugestie: sculele ${tc.label.toLowerCase()} merg de obicei cu această mașină` : `Alocă sculele ${tc.label.toLowerCase()} — rămân pe șantier până la finalizare`}
                data-testid={`tool-color-${c.key}-${tc.code}`}
              >
                {tc.emoji} {tc.label}{selected ? " ✓" : lock ? " 🔒" : ""}
              </button>
            );
          })}
        </div>
        ); })()}
        {/* Status actions strip — different per type */}
        {c.ref_id && (
          <div
            onClick={(ev) => ev.stopPropagation()}
            className="mt-2 pt-2 border-t flex items-center gap-1.5 flex-wrap"
            style={{ borderColor: `${c.color}40` }}
          >
            {c.is_regie ? (
              <>
                <button
                  type="button"
                  disabled={statusBusyKey === c.key}
                  onClick={(e) => { e.stopPropagation(); handleRegieAction(c.key, c.ref_id, "pause", c.label); }}
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors disabled:opacity-50"
                  data-testid={`regie-pause-${c.ref_id}`}
                >
                  ⏸ Pauză
                </button>
                <button
                  type="button"
                  disabled={statusBusyKey === c.key}
                  onClick={(e) => { e.stopPropagation(); handleRegieAction(c.key, c.ref_id, "close", c.label); }}
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-zinc-700 text-white hover:bg-zinc-800 transition-colors disabled:opacity-50"
                  data-testid={`regie-close-${c.ref_id}`}
                >
                  ✓ Termină
                </button>
              </>
            ) : (
              <>
                {String(c.category || "") !== "6.1" && String(c.category || "") !== "7" && (
                  <button
                    type="button"
                    disabled={statusBusyKey === c.key}
                    onClick={() => handleProjectStatus(c.key, c.ref_id, "Finalizat – De demontat", c.label)}
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50"
                    data-testid={`project-finalize-${c.ref_id}`}
                  >
                    🏁 Finalizat – De demontat (6.1)
                  </button>
                )}
                {String(c.category || "") === "6.1" && (
                  <button
                    type="button"
                    disabled={statusBusyKey === c.key}
                    onClick={() => handleProjectStatus(c.key, c.ref_id, "În lucru", c.label)}
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    data-testid={`project-reopen-${c.ref_id}`}
                    title="Întoarce proiectul în categoria 6 (în lucru) — devine disponibil pentru alocări noi"
                  >
                    🔄 Reia (în lucru)
                  </button>
                )}
                {String(c.category || "") !== "7" && (
                  <button
                    type="button"
                    disabled={statusBusyKey === c.key}
                    onClick={() => setDismantleTarget({ cardKey: c.key, refId: c.ref_id, label: c.label, day: dayIso, finalization_date: c.finalization_date })}
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors disabled:opacity-50"
                    data-testid={`project-dismantle-${c.ref_id}`}
                    title="Marchează ca demontat — dispare din Planificator"
                  >
                    🔧 Demontat (7)
                  </button>
                )}
                {String(c.category || "") === "7" && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-emerald-100 text-emerald-700">
                    ✓ Demontat
                  </span>
                )}
              </>
            )}
          </div>
        )}

        {/* SUMAR COMPACT — mereu vizibil la baza cardului de proiect (zone + m² + ce e de făcut) */}
        {!isSelected && c.type === "project" && (
          (Array.isArray(c.zones) && c.zones.length > 0) ||
          (c.work_zones && c.work_zones.trim())
        ) && (() => {
          const totalZones = Array.isArray(c.zones) ? c.zones.length : 0;
          const totalSqm = (c.scope_items || []).reduce((acc, it) => acc + (parseFloat(it?.qty) || 0), 0);
          const fmt = (v) => v.toLocaleString("ro-RO", { maximumFractionDigits: 2 });
          // Agregare m² pe tip pentru badge-uri compacte
          const sqmByType = {};
          (c.scope_items || []).forEach((it) => {
            const q = parseFloat(it?.qty) || 0;
            const cls = it?.classification || {};
            const lbl = cls.label || cls.type || "Altele";
            if (!sqmByType[lbl]) sqmByType[lbl] = 0;
            sqmByType[lbl] += q;
          });
          const typeColors = {
            "EKAS":      { bg: "#FEE2E2", border: "#FCA5A5", text: "#7F1D1D" },
            "Factsheet": { bg: "#DBEAFE", border: "#93C5FD", text: "#1E3A8A" },
            "Merkblatt": { bg: "#FEF3C7", border: "#FCD34D", text: "#78350F" },
          };
          const work = (c.work_zones || "").trim();
          const workShort = work.length > 140 ? work.slice(0, 140) + "…" : work;
          return (
            <div className="mt-2 pt-2 border-t border-zinc-200 text-left space-y-1.5" data-testid={`card-${c.key}-summary`} onClick={(e) => e.stopPropagation()}>
              {totalZones > 0 && (
                <div className="flex flex-wrap gap-1 items-center">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">🗂️ Zone:</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-800">
                    {totalZones} zon{totalZones === 1 ? "ă" : "e"}{totalSqm > 0 ? ` · ${fmt(totalSqm)} m²` : ""}
                  </span>
                  {Object.entries(sqmByType).map(([lbl, sqm]) => {
                    const tc = typeColors[lbl] || { bg: "#F1F5F9", border: "#CBD5E1", text: "#334155" };
                    return (
                      <span key={lbl} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: tc.bg, border: `1px solid ${tc.border}`, color: tc.text }}>
                        {lbl}: {fmt(sqm)} m²
                      </span>
                    );
                  })}
                </div>
              )}
              {work && (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 mb-0.5">📐 Ce e de făcut</div>
                  <div className="text-[11px] text-zinc-800 leading-snug whitespace-pre-wrap bg-zinc-50 border border-zinc-200 rounded px-2 py-1" title={work}>
                    {workShort}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Detalii proiect — apar DOAR pe cardul selectat & dacă există date relevante (zones / lucrări / saci / special) */}
        {isSelected && c.type === "project" && (
          (Array.isArray(c.zones) && c.zones.length > 0) ||
          (c.work_zones && c.work_zones.trim()) ||
          (c.bags_festgebunden > 0 || c.bags_schwachgebunden > 0) ||
          c.requires_post_measurement ||
          c.is_ruckbau ||
          (c.special && c.special.trim())
        ) && (
          <div className="mt-2.5 pt-2.5 border-t border-zinc-200 text-left space-y-2" data-testid={`card-${c.key}-details`} onClick={(e) => e.stopPropagation()}>
            {/* Zone — afișare grupată pe tip cu nr. zone & m² din scope_items */}
            {Array.isArray(c.zones) && c.zones.length > 0 && (() => {
              // Agregare zonelor per tip (EKAS / Factsheet / Merkblatt / Altele) + m² calculați din scope_items
              const totalZones = c.zones.reduce((s, z) => s + (Number(z.count) || 1), 0);
              const byType = {};
              for (const z of c.zones) {
                const k = z.type || z.label || "Altele";
                if (!byType[k]) byType[k] = { type: k, label: z.label || k, count: 0, descriptions: [] };
                byType[k].count += Number(z.count) || 1;
                if (z.description) byType[k].descriptions.push({ label: z.label || k, description: z.description });
              }
              // Sumă m²/u din scope_items, grupată pe clasificarea finală (label)
              const sqmByType = {};
              const totalSqm = (c.scope_items || []).reduce((acc, it) => {
                const q = parseFloat(it?.qty) || 0;
                const cls = it?.classification || {};
                const lbl = cls.label || cls.type || "Altele";
                if (!sqmByType[lbl]) sqmByType[lbl] = 0;
                sqmByType[lbl] += q;
                return acc + q;
              }, 0);
              const fmt = (v) => v.toLocaleString("ro-RO", { maximumFractionDigits: 2 });
              const colors = {
                "EKAS":      { bg: "#FEE2E2", border: "#FCA5A5", text: "#7F1D1D" },
                "Factsheet": { bg: "#DBEAFE", border: "#93C5FD", text: "#1E3A8A" },
                "Merkblatt": { bg: "#FEF3C7", border: "#FCD34D", text: "#78350F" },
              };
              return (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
                    🗂️ Zone proiect ({totalZones} zon{totalZones === 1 ? "ă" : "e"}{totalSqm > 0 ? ` · total ${fmt(totalSqm)} m²` : ""})
                  </div>
                  <div className="flex flex-wrap gap-1 mb-1">
                    {Object.values(byType).map((g, i) => {
                      const sqm = sqmByType[g.label] || 0;
                      const c2 = colors[g.type] || { bg: "#F1F5F9", border: "#CBD5E1", text: "#334155" };
                      return (
                        <span key={i} className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: c2.bg, border: `1px solid ${c2.border}`, color: c2.text }}>
                          {g.label}: {g.count} zon{g.count === 1 ? "ă" : "e"}{sqm > 0 ? ` · ${fmt(sqm)} m²` : ""}
                        </span>
                      );
                    })}
                  </div>
                  {Object.values(byType).some((g) => g.descriptions.length > 0) && (
                    <div className="text-[11px] text-zinc-700 leading-snug space-y-0.5">
                      {Object.values(byType).flatMap((g) => g.descriptions).map((d, i) => (
                        <div key={i}>
                          <strong className="text-zinc-900">{d.label}:</strong> {d.description}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Lucrări (work_zones) */}
            {c.work_zones && c.work_zones.trim() && (
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 mb-1">📐 Lucrări de făcut</div>
                <div className="text-[11px] text-zinc-800 leading-snug whitespace-pre-wrap bg-zinc-50 border border-zinc-200 rounded px-2 py-1.5">
                  {c.work_zones.trim()}
                </div>
              </div>
            )}

            {/* Saci + Luftmessung + Rückbau */}
            {(c.bags_festgebunden > 0 || c.bags_schwachgebunden > 0 || c.requires_post_measurement || c.is_ruckbau) && (
              <div className="flex flex-wrap gap-1">
                {c.bags_festgebunden > 0 && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-50 border border-red-200 text-red-900">
                    🛍 Festgebunden: {c.bags_festgebunden}
                  </span>
                )}
                {c.bags_schwachgebunden > 0 && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-50 border border-orange-200 text-orange-900">
                    🛍 Schwachgebunden: {c.bags_schwachgebunden}
                  </span>
                )}
                {c.requires_post_measurement && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-900">
                    🌬️ Luftmessung obligatorie
                  </span>
                )}
                {c.is_ruckbau && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-50 border border-violet-200 text-violet-900">
                    🏗 Rückbau
                  </span>
                )}
              </div>
            )}

            {/* Note speciale */}
            {c.special && c.special.trim() && (
              <div className="bg-red-50 border-l-4 border-red-600 rounded px-2 py-1.5">
                <div className="text-[9px] font-extrabold uppercase tracking-wider text-red-700 mb-0.5">⚠️ Note speciale</div>
                <div className="text-[11px] text-red-900 font-semibold leading-snug whitespace-pre-wrap">{c.special.trim()}</div>
              </div>
            )}
          </div>
        )}
      </button>
    );
  };

  // 🧰 Carduri PROIECT cu echipă/mașini dar FĂRĂ scule alocate — la regii sculele NU sunt obligatorii
  const cardsMissingTools = cards.filter(
    (c) => !c.is_regie && !c.regie_id && ((c.person_ids || []).length > 0 || (c.car_ids || []).length > 0) && !c.allocation_color
  );
  const confirmCloseWithMissingTools = () => {
    if (cardsMissingTools.length === 0) return true;
    return window.confirm(
      `⚠️ ATENȚIE — NU AI ALOCAT SCULELE la:\n\n${cardsMissingTools.map((c) => `• ${c.label}`).join("\n")}\n\nSculele rămân pe șantier până la finalizare — alocă o culoare!\n\nÎnchizi totuși fără scule?`
    );
  };

  return (
    <Dialog open onOpenChange={(v) => {
      if (v) return;
      if (!confirmCloseWithMissingTools()) return;
      // Dacă există carduri dirty sau salvări în curs — flush sincron înainte de a închide.
      // Pe mobile asta previne pierderea modificărilor dacă utilizatorul închide repede dialogul.
      if (dirtyCount > 0 || savingCount > 0) {
        flushAllPendingSync();
        toast.info(`💾 Salvez ${dirtyCount || savingCount} modificare(modificări) — închiderea va aștepta...`);
        // Permitem totuși închiderea (fetch keepalive trimite în background),
        // dar lăsăm ~1s ca tick-ul verde să apară dacă a fost rapid.
        setTimeout(() => onClose(), 800);
        return;
      }
      onClose();
    }}>
      <DialogContent
        className="max-w-[98vw] w-[98vw] h-[95vh] flex flex-col p-0"
        onInteractOutside={(e) => {
          // Previne închiderea dialogului când utilizatorul interacționează cu sub-popover-uri
          // (QuickEditPopover, datepicker, native window.prompt/confirm care declanșează
          // pointer-down-outside) sau face click pe `[data-sonner-toast]`/Radix overlays.
          const t = e.target;
          if (!t) return;
          const closest = (sel) => (t.nodeType === 1 ? t.closest(sel) : null);
          if (
            closest('[data-radix-popper-content-wrapper]') ||
            closest('[data-radix-popover-content]') ||
            closest('[data-radix-select-content]') ||
            closest('[data-sonner-toast]') ||
            closest('[role="alertdialog"]')
          ) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Dacă există un popover deschis în interior, lasă popover-ul să se închidă singur.
          const openPopover = document.querySelector('[data-radix-popper-content-wrapper] [data-state="open"]');
          if (openPopover) e.preventDefault();
        }}
      >
        <DialogHeader className="px-5 py-3 border-b border-[var(--border)] flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays size={18} className="text-[var(--primary)]"/>
            Alocă personal pentru <strong>{day}</strong>
            <span className="text-xs font-normal text-zinc-500 ml-3">
              {regiiCards.length} regii · {projectCards.length} proiecte active
            </span>
            {/* Indicator stare salvare */}
            {(dirtyCount > 0 || savingCount > 0) ? (
              <span
                className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 inline-flex items-center gap-1"
                data-testid="saving-indicator"
                title="Modificările se salvează automat — așteaptă confirmarea înainte de a închide"
              >
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse"/>
                Se salvează... ({dirtyCount + savingCount})
              </span>
            ) : (
              <span
                className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1"
                data-testid="saved-indicator"
              >
                ✓ Totul salvat
              </span>
            )}
            {selectedCard && (
              <span className="ml-auto text-xs font-semibold px-3 py-1 rounded-full" style={{ background: `${selectedCard.color}20`, color: selectedCard.color }}>
                Selectat: {selectedCard.label}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-3 p-3 flex-1 overflow-hidden min-h-0">
          {/* LEFT: Cards (regii sus, proiecte jos) */}
          <div className="overflow-y-auto pr-2 space-y-4">
            <section>
              <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1 z-10 border-b border-[var(--border)]">
                <h3 className="font-display font-bold text-sm uppercase tracking-wider text-amber-700">
                  📋 Regii ({regiiCards.length})
                </h3>
                <span className="text-[10px] text-zinc-400">click pe card → alocă persoane</span>
              </div>
              {regiiCards.length === 0 ? (
                <div className="text-xs text-zinc-400 italic p-3 bg-zinc-50 rounded">Nicio regie activă în această zi.</div>
              ) : (
                <div className="space-y-3">
                  {regiiByFirma.map(([firma, list]) => (
                    <div key={firma} data-testid={`firma-group-${firma.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="flex items-center gap-1.5 mb-1.5 pb-0.5 border-b border-zinc-200">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-600">
                          🏢 {firma}
                        </span>
                        <span className="text-[10px] text-zinc-400">({list.length})</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {list.map(renderCard)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Quick-add regie — deschide dialog complet identic cu cel din /proiecte */}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowAddRegie(true)}
                  className="w-full text-left rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 hover:bg-amber-100 p-3 transition-colors text-amber-800 font-semibold text-sm flex items-center gap-2"
                  data-testid="add-regie-inline"
                >
                  <Plus size={16}/> Adaugă regie
                </button>
              </div>
            </section>
            {pausedRegii.length > 0 && (
              <section data-testid="paused-regii-section">
                <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1 z-10 border-b border-[var(--border)]">
                  <h3 className="font-display font-bold text-sm uppercase tracking-wider text-zinc-500">
                    ⏸ În pauză ({pausedRegii.length})
                  </h3>
                  <span className="text-[10px] text-zinc-400">scoate din pauză ca să poți aloca din nou</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {pausedRegii.map((r) => {
                    const pauses = Array.isArray(r.pauses) ? r.pauses : [];
                    const cur = pauses.find((p) => (p.start_date || "") <= day && day <= (p.end_date || "9999-12-31"));
                    return (
                      <div
                        key={`paused-${r.id}`}
                        className="rounded-lg border-2 border-zinc-300 bg-zinc-100 p-3 flex flex-col gap-1.5"
                        style={{ filter: "saturate(0.6)" }}
                        data-testid={`paused-regie-${r.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: r.color || "#F59E0B" }}/>
                          <span className="font-bold text-sm text-zinc-700 truncate">{r.name || "Regie"}</span>
                          {r.firma && <span className="text-[10px] text-zinc-500 truncate">· {r.firma}</span>}
                        </div>
                        <div className="text-[11px] text-zinc-500">
                          ⏸ Pauză {cur ? `${fmtRoDate(cur.start_date)} → ${cur.end_date ? fmtRoDate(cur.end_date) : "nedefinit"}` : "activă"}
                          {cur?.note ? ` · ${cur.note}` : ""}
                        </div>
                        <button
                          type="button"
                          disabled={statusBusyKey === `paused-${r.id}`}
                          onClick={() => handleRegieAction(`paused-${r.id}`, r.id, "resume", r.name)}
                          className="self-start text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
                          data-testid={`resume-regie-${r.id}`}
                        >
                          ▶ Scoate din pauză
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
            <section>
              <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1 z-10 border-b border-[var(--border)]">
                <h3 className="font-display font-bold text-sm uppercase tracking-wider text-blue-700">
                  🏗️ Proiecte / Șantiere ({projectCards.length})
                </h3>
                <span className="text-[10px] text-zinc-400">click pe card → alocă persoane</span>
              </div>
              {projectCards.length === 0 ? (
                <div className="text-xs text-zinc-400 italic p-3 bg-zinc-50 rounded">Niciun șantier activ în această zi.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {projectCards.map(renderCard)}
                </div>
              )}
            </section>
          </div>

          {/* RIGHT: Persoane / Autoturisme */}
          <div className="border-l border-[var(--border)] pl-3 flex flex-col overflow-hidden min-h-0">
            {selectedCard && (
              <div className="mb-2 flex-shrink-0">
                {!showNoteEditor ? (
                  <button
                    type="button"
                    onClick={() => setShowNoteEditor(true)}
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border transition-colors w-full max-w-full ${selectedCard.note ? "bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
                    data-testid="card-note-btn"
                    title={selectedCard.note ? `Notă: ${selectedCard.note}` : "Adaugă notă pentru echipă"}
                  >
                    <span className="flex-shrink-0">📝</span>
                    <span className="truncate flex-1 text-left">
                      {selectedCard.note ? selectedCard.note : "Adaugă notă"}
                    </span>
                    <span className="text-[10px] opacity-60 flex-shrink-0">✎</span>
                  </button>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="small-label text-[11px]">📝 Notă pentru echipă (apare pe imaginea distribuită)</Label>
                      <button
                        type="button"
                        onClick={() => setShowNoteEditor(false)}
                        className="text-[10px] text-zinc-500 hover:text-zinc-900 underline"
                        data-testid="card-note-close"
                      >
                        închide
                      </button>
                    </div>
                    <Textarea
                      value={selectedCard.note || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setCards((prev) => prev.map((x) => x.key === selectedCard.key ? { ...x, note: v, dirty: true } : x));
                        // debounce auto-save dacă entry-ul a fost deja persistat
                        if (selectedCard.entryId) {
                          const k = selectedCard.key;
                          if (saveTimersRef.current[k]) clearTimeout(saveTimersRef.current[k]);
                          saveTimersRef.current[k] = setTimeout(() => persistCard(k), 800);
                        }
                      }}
                      placeholder="ex: Gheorghe merge cu echipa Speztec până la magazie"
                      className="min-h-[44px] text-sm"
                      data-testid="card-note-input"
                      autoFocus
                    />
                  </div>
                )}
              </div>
            )}
            <Tabs value={tab} onValueChange={setTab} className="flex flex-col flex-1 overflow-hidden min-h-0">
              <TabsList className="flex-shrink-0">
                <TabsTrigger value="persons" data-testid="tab-persons" title={allocatedPersonsCount > filteredPersons.length ? `⚠ ${allocatedPersonsCount} persoane alocate, doar ${filteredPersons.length} disponibile azi (restul au intrat în concediu/indisponibilitate după alocare)` : `${allocatedPersonsCount} alocate din ${filteredPersons.length} disponibile`}>
                  <Users size={14} className="mr-1.5"/>
                  <span>Persoane ({allocatedPersonsCount}/{filteredPersons.length})</span>
                  {allocatedPersonsCount > filteredPersons.length && (
                    <span className="ml-1.5 text-amber-600 font-bold animate-pulse" title="Persoane alocate care nu mai sunt disponibile (concediu/indisponibilitate)">⚠</span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="cars" data-testid="tab-cars" title="Mașini alocate / disponibile (excluzând cele defect non-drivable)">
                  <CarIcon size={14} className="mr-1.5"/> Autoturisme ({allocatedCarsCount}/{(cars || []).filter((c) => c.status !== "defect" || c.defect_drivable === true).length})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="persons" className="flex-1 overflow-hidden flex flex-col mt-2">
                <div className="relative mb-2 flex-shrink-0">
                  <Search size={14} className="absolute left-2.5 top-2.5 text-zinc-400"/>
                  <Input className="pl-8" placeholder="Caută persoană..." value={qp} onChange={(e) => setQp(e.target.value)} data-testid="search-persons-mass"/>
                </div>
                <div className="flex-1 overflow-y-auto space-y-1">
                  {personsFreeToday.length > 0 && (
                    <div className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 px-1 pt-1 pb-0.5 sticky top-0 bg-white z-10">
                      {isDismantleSelected
                        ? `🔧 Toate persoanele eligibile (demontaj durează ~2h, conflictele permise) (${personsFreeToday.length})`
                        : `✓ Disponibile pentru ${day} (${personsFreeToday.length})`}
                    </div>
                  )}
                  {personsFreeToday.map(renderPersonRow)}
                  {personsBusyToday.length > 0 && (
                    <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 px-1 pt-3 pb-0.5 sticky top-0 bg-white z-10 border-t border-zinc-200 mt-2">
                      Deja alocate / indisponibile azi ({personsBusyToday.length})
                    </div>
                  )}
                  {personsBusyToday.map(renderPersonRow)}
                  {/* Persoane ALOCATE dar care nu mai sunt în lista de disponibili (ex: au intrat în concediu/indisponibilitate după alocare) */}
                  {(() => {
                    const filteredIds = new Set(filteredPersons.map((p) => p.id));
                    const orphanIds = Object.keys(personDayAllocations).filter((pid) => !filteredIds.has(pid));
                    if (orphanIds.length === 0) return null;
                    const allPersonsMap = new Map((persons || []).map((p) => [p.id, p]));
                    return (
                      <>
                        <div className="text-[9px] font-bold uppercase tracking-wider text-amber-800 px-1 pt-3 pb-0.5 sticky top-0 bg-amber-50 z-10 border-t-2 border-amber-300 mt-2 flex items-center gap-1">
                          ⚠ Alocate dar INDISPONIBILE azi ({orphanIds.length}) — recomandă reasignarea
                        </div>
                        {orphanIds.map((pid) => {
                          const p = allPersonsMap.get(pid) || { id: pid, name: "(persoană plecată)" };
                          const allocs = personDayAllocations[pid] || [];
                          return (
                            <div key={`orphan-${pid}`} className="w-full flex items-stretch gap-1 rounded border border-amber-300 bg-amber-50 text-sm">
                              <div className="flex-1 px-2 py-1.5">
                                <div className="font-semibold text-amber-900">
                                  {p.name}
                                  {p.ekas && <span className="ml-1 text-yellow-500" title="EKAS">⭐</span>}
                                  {p.permis_conducere && <span className="ml-1 text-blue-600" title="Permis de conducere">🚗</span>}
                                </div>
                                <div className="flex items-center gap-1 flex-wrap mt-0.5">
                                  {allocs.map((a) => (
                                    <span
                                      key={a.key}
                                      className="text-[10px] px-1.5 py-0.5 rounded text-white font-bold"
                                      style={{ background: a.color || "#F59E0B" }}
                                      title={a.vorarbeiter ? `Vorarbeiter: ${a.vorarbeiter}` : undefined}
                                    >
                                      {a.label}
                                      {a.vorarbeiter && (
                                        <span className="ml-1 px-1 py-0.5 rounded bg-white/25 font-semibold tracking-tight">
                                          👷 {a.vorarbeiter}
                                        </span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                                <div className="text-[10px] text-amber-700 italic mt-0.5">Concediu / indisponibilitate · scoate-o din alocările de mai sus</div>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                  {personsOnLeaveToday.length > 0 && (
                    <div className="text-[9px] font-bold uppercase tracking-wider text-blue-700 px-1 pt-3 pb-0.5 sticky top-0 bg-white z-10 border-t border-zinc-200 mt-2" data-testid="on-leave-section-header">
                      🏖 În concediu / indisponibili pe {day} ({personsOnLeaveToday.length})
                    </div>
                  )}
                  {personsOnLeaveToday.map(renderPersonRow)}
                  {filteredPersons.length === 0 && personsOnLeaveToday.length === 0 && <div className="text-xs text-zinc-400 italic py-4 text-center">Niciun rezultat.</div>}
                </div>
              </TabsContent>
              <TabsContent value="cars" className="flex-1 overflow-hidden data-[state=active]:flex flex-col mt-2 min-h-0">
                <div className="relative mb-2 flex-shrink-0">
                  <Search size={14} className="absolute left-2.5 top-2.5 text-zinc-400"/>
                  <Input className="pl-8" placeholder="Caută autoturism..." value={qc} onChange={(e) => setQc(e.target.value)} data-testid="search-cars-mass"/>
                </div>
                <div className="flex-1 overflow-y-auto space-y-1">
                  {filteredCars.map((c) => {
                    const allocs = carDayAllocations[c.id] || [];
                    const inSelected = selectedCard && selectedCard.car_ids.includes(c.id);
                    const isSuggestedCar = suggestedCarIds.has(c.id) && !inSelected;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggleCar(c.id)}
                        className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded border text-sm transition-colors ${inSelected ? "bg-emerald-50 border-emerald-400 font-semibold" : isSuggestedCar ? "pulse-green-suggest" : "border-zinc-200 hover:bg-zinc-50"}`}
                        data-testid={`mass-pick-car-${c.id}`}
                      >
                        <span className={`w-4 h-4 rounded border ${inSelected ? "bg-emerald-600 border-emerald-600 text-white" : "border-zinc-300"} grid place-items-center text-[10px] flex-shrink-0`}>
                          {inSelected ? "✓" : ""}
                        </span>
                        <span className="flex-1 min-w-0">
                          <div className="truncate">
                            {c.label}
                            {isDuba(c) && <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800" data-testid={`car-duba-${c.id}`}>🚐 DUBĂ</span>}
                            {(c.nr_locuri || 0) >= 8 && <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-800" data-testid={`car-bus-${c.id}`}>🚌 BUS</span>}
                            {c.nr_locuri ? <span className="ml-1.5 text-[10px] font-bold text-zinc-500" data-testid={`car-seats-${c.id}`}>👥 {c.nr_locuri} locuri</span> : null}
                            {isSuggestedCar && (
                              <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900" data-testid={`suggested-car-${c.id}`}>
                                🚗 mașina șoferului alocat
                              </span>
                            )}
                          </div>
                          {c.status === "defect" && (
                            c.defect_drivable
                              ? <span className="text-[9px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" title={c.defect_reason || "Defect — poate fi condusă"}>defect (rulează)</span>
                              : <span className="text-[9px] text-red-600 bg-red-100 px-1.5 py-0.5 rounded" title={c.defect_reason || "Defect — imobilizată"}>defect</span>
                          )}
                          {allocs.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {allocs.map((a) => (
                                <span key={`${c.id}-${a.key}`} className="text-[9px] px-1.5 py-0.5 rounded text-white" style={{ background: a.color }}>{a.label}</span>
                              ))}
                            </div>
                          )}
                        </span>
                      </button>
                    );
                  })}
                  {filteredCars.length === 0 && <div className="text-xs text-zinc-400 italic py-4 text-center">Niciun rezultat.</div>}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <DialogFooter className="px-5 py-3 border-t border-[var(--border)] flex-shrink-0">
          <div className="flex-1 text-xs text-zinc-500 italic">
            ✓ Modificările se salvează automat
            {savingKeys.size > 0 && <span className="ml-2 text-blue-600 font-semibold">· {savingKeys.size} în curs...</span>}
          </div>
          <Button
            onClick={() => { if (!confirmCloseWithMissingTools()) return; onSaved(); }}
            data-testid="mass-save"
            className="!text-red-600 font-bold"
          >
            {cardsMissingTools.length > 0 ? `⚠ Închide (${cardsMissingTools.length} fără scule)` : "Închide"}
          </Button>
        </DialogFooter>
      </DialogContent>
      {dismantleTarget && (
        <DismantleDialog
          target={dismantleTarget}
          persons={persons}
          cars={cars}
          projectId={dismantleTarget.refId}
          onClose={() => setDismantleTarget(null)}
          onSubmit={submitDismantle}
          busy={statusBusyKey === dismantleTarget.cardKey}
        />
      )}
      {pauseTarget && (
        <PauseRegieDialog
          target={pauseTarget}
          onClose={() => setPauseTarget(null)}
          onSubmit={submitPause}
          busy={statusBusyKey === pauseTarget.cardKey}
          defaultStart={dayIso}
        />
      )}
      {driverPickPending && (
        <Dialog open={true} onOpenChange={(o) => {
          if (!o) {
            // Anulare: rezolvăm cu null (nu desemnăm șofer)
            const r = driverPickResolverRef.current;
            driverPickResolverRef.current = null;
            setDriverPickPending(null);
            r && r(null);
          }
        }}>
          <DialogContent className="max-w-md" data-testid="driver-pick-dialog">
            <DialogHeader>
              <DialogTitle>🚗 Cine conduce mașina <span className="text-blue-700">{driverPickPending.carLabel}</span>?</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <div className="text-xs text-zinc-600 mb-2">Sunt {driverPickPending.candidates.length} persoane cu permis în echipă. Selectează șoferul:</div>
              {driverPickPending.candidates.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    const r = driverPickResolverRef.current;
                    driverPickResolverRef.current = null;
                    setDriverPickPending(null);
                    r && r(c.id);
                  }}
                  className="w-full text-left px-3 py-2 rounded border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors flex items-center gap-2"
                  data-testid={`driver-pick-${c.id}`}
                >
                  <span className="text-blue-600">🚗</span>
                  <span className="font-semibold">{c.name}</span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  const r = driverPickResolverRef.current;
                  driverPickResolverRef.current = null;
                  setDriverPickPending(null);
                  r && r(null);
                }}
                className="w-full text-left px-3 py-2 rounded border border-zinc-300 bg-white hover:bg-zinc-50 text-xs text-zinc-600 mt-2"
                data-testid="driver-pick-skip"
              >
                Sar peste (alocă fără șofer desemnat)
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {pendingConflictPid && (
        <Dialog open={true} onOpenChange={(o) => { if (!o) { setPendingConflictPid(null); setConflictSplitTimes(null); } }}>
          <DialogContent
            className="max-w-lg"
            onInteractOutside={(e) => e.preventDefault()}
            data-testid="conflict-dialog"
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="text-amber-600" size={20} />
                Conflict alocare — {pendingConflictPid.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="text-sm text-zinc-700">
                <strong>{pendingConflictPid.name}</strong> este deja alocat(ă) în ziua aceasta la:
              </div>
              <div className="space-y-1.5" data-testid="conflict-existing-allocs">
                {pendingConflictPid.allocs.map((a) => (
                  <div key={a.key} className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-zinc-50 border border-zinc-200">
                    <span
                      className="text-[11px] px-2 py-0.5 rounded text-white font-bold flex-shrink-0"
                      style={{ background: a.color || "#F59E0B" }}
                    >
                      {a.label}
                    </span>
                  </div>
                ))}
              </div>
              <div className="text-xs text-zinc-600 bg-blue-50 border border-blue-200 rounded p-2.5">
                Cum vrei să rezolvi conflictul?
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  <li><strong>Mută complet</strong> — o scoate de la alocările de sus și o pune doar aici.</li>
                  <li><strong>Lucrează la ambele</strong> — rămâne la ambele și poți seta orele (ex: 07–11 acolo, 11–17 aici).</li>
                </ul>
              </div>
              {conflictSplitTimes && (
                <div className="space-y-2 border-2 border-blue-300 bg-blue-50/50 rounded-lg p-3" data-testid="conflict-split-times">
                  <div className="text-xs font-bold text-blue-900 uppercase tracking-wide">⏱ Setează orele pentru fiecare șantier</div>
                  {pendingConflictPid.allocs.map((a) => (
                    <div key={a.card_key} className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] px-2 py-0.5 rounded text-white font-bold flex-shrink-0" style={{ background: a.color || "#F59E0B" }}>{a.label}</span>
                      <span className="text-xs text-zinc-500">de la</span>
                      <Input type="time" className="w-24 h-8" value={conflictSplitTimes.allocTimes[a.card_key]?.from || ""} data-testid={`split-from-${a.card_key}`}
                        onChange={(e) => setConflictSplitTimes((s) => ({ ...s, allocTimes: { ...s.allocTimes, [a.card_key]: { ...(s.allocTimes[a.card_key] || {}), from: e.target.value } } }))}/>
                      <span className="text-xs text-zinc-500">până la</span>
                      <Input type="time" className="w-24 h-8" value={conflictSplitTimes.allocTimes[a.card_key]?.to || ""} data-testid={`split-to-${a.card_key}`}
                        onChange={(e) => setConflictSplitTimes((s) => ({ ...s, allocTimes: { ...s.allocTimes, [a.card_key]: { ...(s.allocTimes[a.card_key] || {}), to: e.target.value }, }, newFrom: s.newFrom || e.target.value }))}/>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-blue-200">
                    <span className="text-[11px] px-2 py-0.5 rounded bg-blue-600 text-white font-bold flex-shrink-0">APOI → {(cardsRef.current.find((c) => c.key === selectedKey)?.label) || "aici"}</span>
                    <span className="text-xs text-zinc-500">de la</span>
                    <Input type="time" className="w-24 h-8" value={conflictSplitTimes.newFrom} data-testid="split-new-from"
                      onChange={(e) => setConflictSplitTimes((s) => ({ ...s, newFrom: e.target.value }))}/>
                    <span className="text-xs text-zinc-500">până la</span>
                    <Input type="time" className="w-24 h-8" value={conflictSplitTimes.newTo} data-testid="split-new-to"
                      onChange={(e) => setConflictSplitTimes((s) => ({ ...s, newTo: e.target.value }))}/>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => { setPendingConflictPid(null); setConflictSplitTimes(null); }}
                data-testid="conflict-cancel"
              >
                Anulează
              </Button>
              {conflictSplitTimes ? (
                <>
                  <Button variant="ghost" onClick={acceptConflictBoth} data-testid="conflict-both-no-times">
                    Fără ore
                  </Button>
                  <Button
                    onClick={acceptConflictBothWithTimes}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="conflict-split-confirm"
                  >
                    ✓ Confirmă orele
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => {
                    const at = {};
                    (pendingConflictPid.allocs || []).forEach((a, i) => { at[a.card_key] = { from: i === 0 ? "07:00" : "", to: i === 0 ? "11:00" : "" }; });
                    setConflictSplitTimes({ allocTimes: at, newFrom: "11:00", newTo: "17:00" });
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="conflict-both"
                >
                  ⏱ Lucrează la ambele (split)
                </Button>
              )}
              <Button
                onClick={acceptConflictMove}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="conflict-move"
              >
                → Mută complet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <RegieFullDialog
        open={showAddRegie}
        onOpenChange={setShowAddRegie}
        defaultStartDate={day}
        onCreated={onRegieCreated}
      />
      <EditMinPeopleDialog
        open={!!editingMinPeople}
        onClose={() => setEditingMinPeople(null)}
        target={editingMinPeople?.target}
        isoDay={editingMinPeople?.isoDay}
        currentMin={editingMinPeople?.currentMin}
        currentMax={editingMinPeople?.currentMax}
        onSaved={() => { setEditingMinPeople(null); onRefresh ? onRefresh() : (onSaved && onSaved()); }}
      />
      {historyFor && historyFor.type === "regie" && (
        <RegieDetailDialog
          regie={(regii || []).find((r) => r.id === historyFor.ref_id) || { id: historyFor.ref_id, name: historyFor.label }}
          onClose={() => setHistoryFor(null)}
        />
      )}
      {historyFor && historyFor.type === "project" && (
        <ProjectDetailDialog
          project={(projects || []).find((p) => p.id === historyFor.ref_id) || { id: historyFor.ref_id, name: historyFor.label }}
          onClose={() => setHistoryFor(null)}
          onOpenHours={() => window.open("/ore", "_blank", "noopener,noreferrer")}
        />
      )}
    </Dialog>
  );
}

// ===== Pause regie dialog: ask for start_date / end_date =====
function PauseRegieDialog({ target, onClose, onSubmit, busy, defaultStart }) {
  const [startDate, setStartDate] = useState(defaultStart || new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  const [hasEnd, setHasEnd] = useState(false);
  const [note, setNote] = useState("");
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="pause-regie-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">⏸ Pauză regie · {target.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2 text-sm">
          <div className="bg-amber-50 border border-amber-300 rounded p-3 text-xs text-amber-900">
            ⚠️ În acest interval, regia <strong>nu va mai apărea în Planificator</strong> și toate alocările existente în acest interval vor fi eliberate.
          </div>
          <div>
            <Label className="text-xs text-zinc-500">📅 Pauză de la</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1" data-testid="pause-start-date"/>
          </div>

          {/* Selector clar între pauză nedefinită și pauză cu dată finalizare */}
          <div>
            <Label className="text-xs text-zinc-500 font-semibold uppercase">Până când?</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button
                type="button"
                onClick={() => { setHasEnd(false); setEndDate(""); }}
                className={`p-3 rounded-lg border-2 text-left transition-all ${!hasEnd ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200" : "border-zinc-200 hover:border-zinc-300 bg-white"}`}
                data-testid="pause-mode-indefinite"
              >
                <div className="text-2xl leading-none">♾️</div>
                <div className="font-bold text-xs mt-1">Nedefinit</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">Până apeși „Reluare”</div>
              </button>
              <button
                type="button"
                onClick={() => setHasEnd(true)}
                className={`p-3 rounded-lg border-2 text-left transition-all ${hasEnd ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200" : "border-zinc-200 hover:border-zinc-300 bg-white"}`}
                data-testid="pause-mode-fixed"
              >
                <div className="text-2xl leading-none">📅</div>
                <div className="font-bold text-xs mt-1">Cu dată finalizare</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">Știi când se reia</div>
              </button>
            </div>
            {hasEnd && (
              <div className="mt-2">
                <Label className="text-xs text-zinc-500">Reia automat începând cu</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} min={startDate} className="mt-1" data-testid="pause-end-date" autoFocus/>
                {endDate && endDate < startDate && (
                  <div className="text-[11px] text-red-600 mt-1">⚠️ Data de finalizare trebuie ≥ data de început</div>
                )}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs text-zinc-500">📝 Notă (opțional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex: Vacanță de Paști, lipsă materiale, ploaie..." className="mt-1" data-testid="pause-note"/>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Anulează</Button>
          <Button
            onClick={() => onSubmit({ start_date: startDate, end_date: hasEnd ? endDate : null, note })}
            disabled={busy || !startDate || (hasEnd && !endDate) || (hasEnd && endDate < startDate)}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="pause-confirm"
          >
            {busy ? "Se aplică..." : (hasEnd && endDate ? `⏸ Pauză până la ${endDate}` : "⏸ Pune în pauză nedefinit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
